import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { Upload, Settings, Eye, Layers, DraftingCompass, FileText, User, MapPin, Check, X, Lock, ArrowLeft, ArrowRight, Menu, Palette, CheckCircle, Mail, ChevronLeft, ChevronRight, Clock, Boxes, SlidersHorizontal, Wrench, Ruler, RulerDimensionLine, Rotate3d, ChevronDown, AlertTriangle, Plus, Trash2, Pin } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Card, CardContent, CardHeader } from '../components/ui/card';
import { Textarea } from '../components/ui/textarea';
import MainNavigation from '../components/MainNavigation';
import Footer from '../components/Footer';
import ThreeDPreview from '../components/ThreeDPreview';
import FilePreview from '../components/FilePreview';
import MultiFileUpload from '../components/MultiFileUpload';
import { BACKEND_URL } from '../config';
import { trackQuoteSubmission, trackServiceRequest } from '../utils/analytics';
import { useCart } from '../contexts/CartContext';
import { useAuth } from '../contexts/AuthContext';

const API = `${BACKEND_URL}/api`;

// Insert size presets (ordered smallest → largest for escalation compounding)
const INSERT_SIZES = {
  metric: ['M2', 'M2.5', 'M3', 'M4', 'M5', 'M6', 'M8', 'M10'],
  imperial: ['#4-40', '#6-32', '#8-32', '#10-24', '#10-32', '1/4-20', '5/16-18', '3/8-16'],
};

// Service definitions for the accordion selection
const SERVICES = [
  {
    id: 'on-demand-3d-print',
    name: 'Instant Quote',
    description: 'Upload & get pricing instantly',
    fullDescription: 'Get an instant quote based on an intelligent automated analysis of your CAD file and choose from our recommended, preset printing settings.',
    icon: Upload,
    badge: 'Most Popular',
    badgeColor: 'green',
    supportedFormats: 'STL, OBJ, 3MF, STEP, PLY files supported',
    whatsIncluded: [
      'CAD File analysis and 3D Preview for quick validation',
      'Powerful CAD editing tools including scaling and orientation available',
      'Carefully selected preset print settings and material selection based on your project requirements',
      'Post-processing options including smoothing, painting, and finishing',
      'Fast turnaround times with production time estimates before order placement',
      'Comprehensive professional review of your CAD file and print settings chosen for each on-demand print order',
      'Your IP is fully protected and retained'
    ],
    additionalSections: [
      {
        title: 'Materials Available:',
        content: 'We offer a comprehensive range of FDM materials including PLA, PLA+, Silk PLA, PETG, ABS, ASA, TPU (flexible), Carbon Fiber reinforced filaments (PLA-CF, PETG-CF, PPS-CF), Wood-fill, Metal-fill, and high-temperature materials.'
      },
      {
        title: 'Quality Options:',
        type: 'grid',
        items: [
          { name: 'Concept', desc: 'Fast prints for initial form and fit checks' },
          { name: 'Prototype', desc: 'Balanced quality for functional testing' },
          { name: 'Production', desc: 'High quality for end-use parts' },
          { name: 'Display', desc: 'Premium finish for presentation pieces' }
        ]
      }
    ]
  },
  {
    id: 'design',
    name: 'Custom Design',
    description: 'Professional 3D modeling service',
    fullDescription: 'Complete design-to-production services with engineering expertise in functional, cosmetic, and load-bearing applications.',
    icon: DraftingCompass,
    badge: 'Full Service',
    badgeColor: 'purple',
    whatsIncluded: [
      'Custom product design from concept sketches or verbal descriptions',
      'Functional prototyping and iterative design refinement',
      'Your IP is fully protected and retained'
    ],
    additionalSections: [
      {
        title: 'Design Capabilities:',
        type: 'grid',
        items: [
          { name: 'Mechanical Parts', desc: 'Gears, brackets, housings, enclosures, and functional assemblies' },
          { name: 'Consumer Products', desc: 'Product housings, ergonomic designs, and aesthetic components' },
          { name: 'Custom Fixtures', desc: 'Jigs, fixtures, tooling, and manufacturing aids' }
        ]
      },
      {
        title: 'Deliverables:',
        type: 'list',
        items: [
          'Native CAD files (STEP, Fusion 360, SolidWorks compatible)',
          'Print-ready files with optimized settings',
          'Material and print setting recommendations',
          'Printed prototypes or finished components (optional)'
        ]
      }
    ]
  },
  {
    id: 'convert',
    name: '2D to 3D',
    description: 'Convert drawings to models',
    fullDescription: 'Expert engineering services to transform sketches, 2D CAD designs, and technical or shop drawings into optimized 3D models ready for 3D printing production.',
    icon: Layers,
    badge: 'Conversion',
    badgeColor: 'blue',
    whenToConsider: [
      'You have technical drawings or blueprints that need to become physical parts',
      'Legacy products need to be recreated from original 2D documentation',
      'Hand sketches or concepts need to be turned into manufacturable designs',
      'You\'re transitioning from traditional manufacturing to 3D printing'
    ],
    whatsIncluded: [
      'Accurate conversion from 2D drawings, blueprints, sketches, or technical drawings',
      'Engineering-grade CAD modeling with proper dimensions and tolerances',
      'Your IP is fully protected and retained'
    ],
    additionalSections: [
      {
        title: 'Optional Services:',
        type: 'grid',
        items: [
          { name: 'Printable Design', desc: 'Design for manufacturability (DFM) optimization for 3D printing' },
          { name: 'Consulting', desc: 'Consulting on your design for component optimization opportunities' },
          { name: 'Prototype', desc: '3D printing a prototype of the created 3D model for review' }
        ]
      },
      {
        title: 'Accepted Input Formats:',
        content: 'PDF drawings, scanned blueprints, hand sketches (photographed), DWG/DXF files, technical specifications, and even verbal descriptions with reference images.'
      }
    ]
  },
  {
    id: 'optimize',
    name: 'Optimize',
    description: 'Improve your 3D models',
    fullDescription: 'Engineering analysis and component optimization for improved performance, durability, and printing results as well as consistency.',
    icon: Settings,
    badge: 'Enhancement',
    badgeColor: 'amber',
    whenToConsider: [
      'Your existing design wasn\'t created with 3D printing in mind',
      'Parts are failing or not meeting performance requirements',
      'You need to reduce weight for aerospace, automotive, or portable applications',
      'Production costs need to be reduced for volume manufacturing',
      'You have a model or part that doesn\'t fit on your printer bed'
    ],
    whatsIncluded: [
      'Modification or optimization for print orientation and support structure',
      'Structural reinforcement for load-bearing and high-stress applications',
      'Weight reduction where possible through structure and material optimization',
      'Model splitting for large components or assemblies and structures',
      'Your IP is fully protected and retained'
    ],
    additionalSections: [
      {
        title: 'Optimization Benefits:',
        type: 'grid',
        items: [
          { name: 'Weight Reduction', desc: 'Reduce material usage while maintaining structural integrity' },
          { name: 'Strength Enhancement', desc: 'Strategic reinforcement to improve load-bearing capacity' },
          { name: 'Print Optimization', desc: 'Optimal orientation, support minimization for better print quality' },
          { name: 'Cost Reduction', desc: 'Material efficiency improvements and print time optimization' }
        ]
      }
    ]
  },
  {
    id: 'renders',
    name: 'Renders',
    description: 'Photorealistic product images',
    fullDescription: 'Realistic visualization services to showcase your product designs before manufacturing, perfect for presentations and marketing.',
    icon: Eye,
    badge: 'Visualization',
    badgeColor: 'pink',
    useCases: [
      'Crowdfunding campaigns and investor presentations',
      'E-commerce product listings and marketing materials',
      'Design validation before committing to physical prototypes',
      'Client presentations and design approval processes',
      'Visualizing 3D printed end-use parts for aesthetic validation'
    ],
    whatsIncluded: [
      'High-quality photorealistic renders with accurate materials and lighting',
      'Multiple viewing angles and environmental settings for comprehensive visualization',
      'Product animation and 360-degree rotation videos available',
      'Marketing-ready images for presentations, websites, and promotional materials',
      'Your IP is fully protected and retained'
    ],
    additionalSections: [
      {
        title: 'Render Services:',
        type: 'grid',
        items: [
          { name: 'Product Pictures', desc: 'Static renders from different angles with studio lighting' },
          { name: 'Lifestyle Pictures', desc: 'Environmental renders to showcase product in realistic settings' },
          { name: 'Animations', desc: '360° turntable or product assembly and demonstration animations' }
        ]
      }
    ]
  }
];

// Service Selection Accordion Component
const ServiceSelectionAccordion = ({ selectedService, onServiceSelect, onLaunch }) => {
  const [expandedService, setExpandedService] = useState(selectedService || 'on-demand-3d-print');

  const handleServiceClick = (serviceId) => {
    setExpandedService(expandedService === serviceId ? null : serviceId);
  };

  const getBadgeColor = (color) => {
    const colors = {
      green: 'bg-emerald-50 text-emerald-600',
      purple: 'bg-purple-50 text-purple-600',
      blue: 'bg-blue-50 text-blue-600',
      amber: 'bg-amber-50 text-amber-600',
      pink: 'bg-pink-50 text-pink-600'
    };
    return colors[color] || colors.blue;
  };

  return (
    <div className="max-w-4xl mx-auto">
      <div className="flex flex-col gap-3">
        {SERVICES.map((service) => {
          const isExpanded = expandedService === service.id;
          const Icon = service.icon;
          
          return (
            <div 
              key={service.id}
              className={`bg-white rounded-2xl overflow-hidden transition-all duration-300 ${
                isExpanded 
                  ? 'shadow-xl shadow-blue-500/10 ring-1 ring-blue-100' 
                  : 'shadow-md hover:shadow-lg'
              }`}
            >
              {/* Accordion Header */}
              <button
                type="button"
                onClick={() => handleServiceClick(service.id)}
                className={`w-full flex items-center gap-4 p-5 transition-all duration-300 ${
                  isExpanded 
                    ? 'bg-gradient-to-r from-[#0007b5] to-[#0015e6] text-white' 
                    : 'bg-white hover:bg-slate-50 text-slate-900'
                }`}
              >
                <div className={`w-14 h-14 rounded-xl flex items-center justify-center transition-all duration-300 ${
                  isExpanded 
                    ? 'bg-white/20' 
                    : 'bg-slate-100'
                }`}>
                  <Icon className={`w-6 h-6 ${isExpanded ? 'text-white' : 'text-slate-500'}`} />
                </div>
                <div className="flex-1 text-left">
                  <h3 className={`text-lg font-semibold ${isExpanded ? 'text-white' : 'text-slate-900'}`}>
                    {service.name}
                  </h3>
                  <p className={`text-sm ${isExpanded ? 'text-blue-100' : 'text-slate-500'}`}>
                    {service.description}
                  </p>
                </div>
                <ChevronDown className={`w-5 h-5 transition-transform duration-300 ${
                  isExpanded ? 'rotate-180 text-white' : 'text-slate-400'
                }`} />
              </button>

              {/* Accordion Content */}
              <div className={`overflow-hidden transition-all duration-300 ease-in-out ${
                isExpanded ? 'max-h-[1500px] opacity-100' : 'max-h-0 opacity-0'
              }`}>
                <div className="p-6 pt-6">
                  {/* Description */}
                  <p className="text-slate-600 mb-6 leading-relaxed">
                    {service.fullDescription}
                  </p>

                  {/* Supported Formats (for Instant Quote) */}
                  {service.supportedFormats && (
                    <p className="text-sm text-slate-500 mb-6 italic">
                      {service.supportedFormats}
                    </p>
                  )}

                  {/* Use Cases (for Renders - moved above What's Included) */}
                  {service.useCases && (
                    <div className="mb-6">
                      <h4 className="font-semibold text-slate-900 mb-4">Use Cases:</h4>
                      <ul className="space-y-2 text-slate-600">
                        {service.useCases.map((item, idx) => (
                          <li key={idx} className="flex items-start">
                            <span className="text-[#0007b5] mr-2">•</span>
                            <span>{item}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* When to Consider (for 2D to 3D and Optimize) */}
                  {service.whenToConsider && (
                    <div className="mb-6">
                      <h4 className="font-semibold text-slate-900 mb-4">
                        {service.id === 'convert' ? 'When to Consider 2D to 3D Conversion:' : 'When to Consider Optimization:'}
                      </h4>
                      <ul className="space-y-2 text-slate-600">
                        {service.whenToConsider.map((item, idx) => (
                          <li key={idx} className="flex items-start">
                            <span className="text-[#0007b5] mr-2">•</span>
                            <span>{item}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* What's Included */}
                  {service.whatsIncluded && (
                    <div className="mb-6">
                      <h4 className="font-semibold text-slate-900 mb-4">What's Included:</h4>
                      <ul className="space-y-3 text-slate-600">
                        {service.whatsIncluded.map((item, idx) => (
                          <li key={idx} className="flex items-start">
                            <span className="text-[#0007b5] mr-2">•</span>
                            <span>{item}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Additional Sections */}
                  {service.additionalSections && service.additionalSections.map((section, sIdx) => (
                    <div key={sIdx} className="mb-6">
                      <h4 className="font-semibold text-slate-900 mb-4">{section.title}</h4>
                      
                      {section.content && (
                        <p className="text-slate-600">{section.content}</p>
                      )}
                      
                      {section.type === 'grid' && section.items && (
                        <div className="grid md:grid-cols-2 gap-4">
                          {section.items.map((item, iIdx) => (
                            <div key={iIdx} className="bg-slate-50 rounded-lg p-4">
                              <span className="font-medium text-slate-900">{item.name}</span>
                              <p className="text-sm text-slate-600">{item.desc}</p>
                            </div>
                          ))}
                        </div>
                      )}
                      
                      {section.type === 'list' && section.items && (
                        <ul className="space-y-2 text-slate-600">
                          {section.items.map((item, iIdx) => (
                            <li key={iIdx} className="flex items-start">
                              <span className="text-[#0007b5] mr-2">•</span>
                              <span>{item}</span>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  ))}

                  {/* Launch Button */}
                  <button
                    type="button"
                    onClick={() => onLaunch(service.id)}
                    className="w-full flex items-center justify-center gap-2 px-6 py-4 bg-[#0007b5] text-white rounded-xl font-semibold hover:bg-[#0010d9] transition-all duration-200 shadow-lg shadow-blue-500/25 hover:shadow-xl hover:shadow-blue-500/30 hover:-translate-y-0.5"
                  >
                    Launch {service.name}
                    <ArrowRight className="w-5 h-5" />
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// Warning Modal Component
const SwitchWarningModal = ({ isOpen, onConfirm, onCancel }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onCancel} />
      <div className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 animate-in fade-in zoom-in-95 duration-200">
        <div className="flex items-center gap-4 mb-4">
          <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center">
            <AlertTriangle className="w-6 h-6 text-amber-600" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-slate-900">Switch Service?</h3>
            <p className="text-sm text-slate-500">Your input may be lost</p>
          </div>
        </div>
        <p className="text-slate-600 mb-6">
          You have unsaved changes in the current service. Switching to another service could reset your progress.
        </p>
        <div className="flex gap-3">
          <button
            onClick={onCancel}
            className="flex-1 px-4 py-3 border border-slate-200 text-slate-700 rounded-xl font-medium hover:bg-slate-50 transition-colors"
          >
            Stay Here
          </button>
          <button
            onClick={onConfirm}
            className="flex-1 px-4 py-3 bg-[#0007b5] text-white rounded-xl font-medium hover:bg-[#0010d9] transition-colors"
          >
            Switch Anyway
          </button>
        </div>
      </div>
    </div>
  );
};

// Shared success-card component used by the service-request wizards
// (Design, 2D→3D, Optimize, Renders). Renders the same layout as the
// Instant Quote results page.
const ServiceRequestSuccessCard = ({
  serviceLabel,
  breadcrumbLabel,
  snapshot,
  requestId,
  rows,                // array of { label, value } - filtered out when value empty
  onBackToServices,
  onSubmitAnother,
  submitAnotherLabel = "Submit Another Request"
}) => {
  const filtered = (rows || []).filter(r => r && r.value !== undefined && r.value !== null && r.value !== '');
  return (
    <>
      {onBackToServices && (
        <div className="bg-gradient-to-r from-slate-50 to-slate-100/80 px-6 md:px-8 py-3 border-b border-slate-200">
          <div className="flex items-center gap-3">
            <button
              onClick={onBackToServices}
              className="inline-flex items-center gap-2 text-[#0007b5] hover:text-[#0015e6] font-semibold text-sm transition-colors group"
            >
              <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
              All Services
            </button>
            <div className="w-0 h-0 border-t-[5px] border-t-transparent border-b-[5px] border-b-transparent border-l-[6px] border-l-slate-300" />
            <span className="text-slate-700 font-semibold text-sm">{breadcrumbLabel || serviceLabel}</span>
          </div>
        </div>
      )}
      <div className="p-6 md:p-10" data-testid="service-request-success">
        <Card>
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center">
                <Check className="w-6 h-6" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-slate-900">Request received</h2>
                <p className="text-sm text-slate-500">
                  Thanks {snapshot?.name || 'there'} — we'll get in touch within 24 hours.
                </p>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="text-center pt-4 pb-2">
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-widest mb-2">
                Your {serviceLabel} Request
              </p>
              {requestId && (
                <p className="text-xs text-slate-500" data-testid="service-request-id">
                  Request #{String(requestId).substring(0, 8).toUpperCase()}
                </p>
              )}
              {snapshot?.email && (
                <p className="text-sm text-slate-500 mt-1">Confirmation sent to {snapshot.email}</p>
              )}
            </div>

            {filtered.length > 0 && (
              <div className="mt-6 p-5 bg-slate-50 border border-slate-200 rounded-xl">
                <div className="flex items-center gap-2 mb-3">
                  <FileText className="w-4 h-4 text-[#0007b5]" />
                  <p className="text-sm font-semibold text-slate-900">Request summary</p>
                </div>
                <dl className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-2 text-sm">
                  {filtered.map((r, i) => (
                    <React.Fragment key={i}>
                      <dt className="text-slate-500">{r.label}</dt>
                      <dd className="text-slate-900 font-medium">{String(r.value)}</dd>
                    </React.Fragment>
                  ))}
                </dl>
              </div>
            )}

            {snapshot?.description && (
              <div className="mt-4 p-5 bg-white border border-slate-200 rounded-xl">
                <p className="text-sm font-semibold text-slate-900 mb-2">Project description</p>
                <p className="text-sm text-slate-700 whitespace-pre-wrap">{snapshot.description}</p>
              </div>
            )}

            <div className="mt-6 p-4 bg-blue-50 border border-blue-100 rounded-xl text-sm text-slate-700">
              <p>
                <strong>What's next?</strong> Our team will review your request and reach out
                at <span className="font-semibold text-[#0007b5]">{snapshot?.email}</span> within 24 hours
                to scope timing, pricing and deliverables.
              </p>
            </div>

            <div className="mt-6 flex flex-col sm:flex-row gap-3 justify-center items-stretch">
              <Button
                className="bg-[#0007b5] hover:bg-[#000690]"
                onClick={onSubmitAnother}
                data-testid="service-submit-another-btn"
              >
                {submitAnotherLabel}
              </Button>
              {onBackToServices && (
                <Button
                  variant="ghost"
                  onClick={onBackToServices}
                  data-testid="service-back-to-services-btn"
                  className="text-slate-600 hover:text-slate-900"
                >
                  Back to All Services
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </>
  );
};


// Styled Selector Component
const StyledSelector = ({ value, onChange, options, placeholder }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [hoveredId, setHoveredId] = useState(null);
  const selectedOption = options.find(opt => opt.id === value);

  return (
    <div className="relative mt-2">
      {/* Trigger Button - Same height as Material selector */}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={`w-full flex items-center justify-between px-4 py-4 bg-white border-2 rounded-xl transition-all duration-300 text-left group min-h-[66px] ${
          isOpen ? 'border-[#0007b5] shadow-lg shadow-blue-500/10' : 'border-slate-200 hover:border-slate-300'
        }`}
      >
        <span className={selectedOption ? 'text-slate-900 font-semibold' : 'text-slate-400'}>
          {selectedOption ? (
            <span>
              {selectedOption.name}
              {selectedOption.subtitle && <span className="text-slate-400 font-normal ml-2 text-sm">{selectedOption.subtitle}</span>}
            </span>
          ) : placeholder}
        </span>
        {/* Arrow with animation - Same as Material selector */}
        <div className={`w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center transition-all duration-300 ${isOpen ? 'bg-[#0007b5] rotate-180' : 'group-hover:bg-slate-200'}`}>
          <svg className={`w-4 h-4 transition-all duration-300 ${isOpen ? 'text-white rotate-90' : 'text-slate-400'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </div>
      </button>
      
      {/* Expandable Panel with Fluid Animation - Same as Material selector */}
      <div 
        className={`overflow-hidden transition-all duration-500 ease-out ${
          isOpen ? 'max-h-[400px] opacity-100 mt-3' : 'max-h-0 opacity-0 mt-0'
        }`}
      >
        <div className="bg-white rounded-2xl border-2 border-slate-100 shadow-xl overflow-hidden">
          {/* Options List with staggered animation */}
          <div className="p-3 space-y-1 max-h-[300px] overflow-y-auto">
            {options.map((option, index) => {
              const isSelected = value === option.id;
              const isHovered = hoveredId === option.id;
              return (
                <button
                  key={option.id}
                  type="button"
                  onClick={() => {
                    onChange(option.id);
                    setIsOpen(false);
                  }}
                  onMouseEnter={() => setHoveredId(option.id)}
                  onMouseLeave={() => setHoveredId(null)}
                  className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-300 text-left ${
                    isSelected 
                      ? 'bg-[#0007b5] shadow-lg' 
                      : isHovered
                        ? 'bg-slate-50'
                        : 'bg-white hover:bg-slate-50'
                  }`}
                  style={{
                    transitionDelay: `${index * 20}ms`,
                    transform: isOpen ? 'translateX(0)' : 'translateX(-10px)',
                    opacity: isOpen ? 1 : 0,
                  }}
                >
                  <div className="flex-1 min-w-0">
                    <div className={`font-medium transition-colors duration-300 ${isSelected ? 'text-white' : 'text-slate-900'}`}>
                      {option.name}
                    </div>
                    {option.subtitle && (
                      <div className={`text-xs transition-colors duration-300 ${isSelected ? 'text-blue-200' : 'text-slate-400'}`}>
                        {option.subtitle}
                      </div>
                    )}
                  </div>
                  {isSelected && (
                    <Check className="w-5 h-5 text-white flex-shrink-0" />
                  )}
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

// Material Swatch Selector - Full Version with Images
const MaterialSwatchSelector = ({ value, onChange, inventory = [], catalog = [] }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [hoveredId, setHoveredId] = useState(null);
  
  const materials = [
    { 
      id: 'PLA', 
      name: 'PLA', 
      subtitle: 'Standard',
      color: '#10b981',
      gradient: 'from-emerald-400 to-emerald-500',
      bestFor: 'Display models',
      thumbnail: 'https://customer-assets.emergentagent.com/job_be0e1b02-3a9b-4b4f-bc88-46ae16a80066/artifacts/p1va6342_PLA%201.jpg'
    },
    { 
      id: 'PLA+', 
      name: 'PLA+', 
      subtitle: 'Reinforced',
      color: '#059669',
      gradient: 'from-emerald-500 to-emerald-600',
      bestFor: 'Light functional parts',
      thumbnail: 'https://customer-assets.emergentagent.com/job_be0e1b02-3a9b-4b4f-bc88-46ae16a80066/artifacts/p1va6342_PLA%201.jpg'
    },
    { 
      id: 'Silk PLA', 
      name: 'Silk PLA', 
      subtitle: 'Glossy finish',
      color: '#ec4899',
      gradient: 'from-pink-400 via-purple-400 to-cyan-400',
      bestFor: 'Visual pieces',
      thumbnail: 'https://customer-assets.emergentagent.com/job_be0e1b02-3a9b-4b4f-bc88-46ae16a80066/artifacts/7zha3wvf_Silk%20PLA%201.jpg'
    },
    { 
      id: 'High Temp PLA', 
      name: 'High Temp PLA', 
      subtitle: 'Heat resistant',
      color: '#dc2626',
      gradient: 'from-red-500 to-red-600',
      bestFor: 'Heat-exposed PLA parts',
      thumbnail: 'https://customer-assets.emergentagent.com/job_be0e1b02-3a9b-4b4f-bc88-46ae16a80066/artifacts/1wiyqx0r_High%20temp%20PLA%201.jpg'
    },
    { 
      id: 'PPS-CF', 
      name: 'PPS-CF', 
      subtitle: 'High Temp CF',
      color: '#0f172a',
      gradient: 'from-slate-800 to-slate-950',
      bestFor: 'Heat-exposed functional parts',
      thumbnail: 'https://customer-assets.emergentagent.com/job_be0e1b02-3a9b-4b4f-bc88-46ae16a80066/artifacts/hzizu6d0_PPS%20CF%201.jpg'
    },
    { 
      id: 'PLA-CF', 
      name: 'PLA-CF', 
      subtitle: 'Carbon Fiber Reinforced',
      color: '#1e293b',
      gradient: 'from-slate-700 to-slate-900',
      bestFor: 'Stiff parts, matte finish',
      thumbnail: 'https://customer-assets.emergentagent.com/job_be0e1b02-3a9b-4b4f-bc88-46ae16a80066/artifacts/2dltkor1_CF%20PLA%201.jpg'
    },
    { 
      id: 'Metal-filled PLA', 
      name: 'Metal-filled PLA', 
      subtitle: 'Metal finish',
      color: '#71717a',
      gradient: 'from-zinc-400 to-zinc-600',
      bestFor: 'Decorative pieces',
      thumbnail: 'https://customer-assets.emergentagent.com/job_be0e1b02-3a9b-4b4f-bc88-46ae16a80066/artifacts/vbknny9d_Metallic%20PLA%201.jpg'
    },
    { 
      id: 'Wood-filled PLA', 
      name: 'Wood-filled PLA', 
      subtitle: 'Wood texture',
      color: '#a16207',
      gradient: 'from-amber-600 to-amber-800',
      bestFor: 'Decorative wood pieces',
      thumbnail: 'https://customer-assets.emergentagent.com/job_be0e1b02-3a9b-4b4f-bc88-46ae16a80066/artifacts/np9lty38_Wood%20PLA%201.jpg'
    },
    { 
      id: 'PETG', 
      name: 'PETG', 
      subtitle: 'Durable',
      color: '#0ea5e9',
      gradient: 'from-sky-400 to-sky-500',
      bestFor: 'Functional parts',
      thumbnail: 'https://customer-assets.emergentagent.com/job_be0e1b02-3a9b-4b4f-bc88-46ae16a80066/artifacts/oar2gs6e_PETG%201.jpg'
    },
    { 
      id: 'PETG-CF', 
      name: 'PETG-CF', 
      subtitle: 'Carbon Fiber',
      color: '#334155',
      gradient: 'from-slate-600 to-slate-800',
      bestFor: 'Functional Parts',
      thumbnail: 'https://customer-assets.emergentagent.com/job_be0e1b02-3a9b-4b4f-bc88-46ae16a80066/artifacts/c8kgu1ce_PETG%20CF%201.jpg'
    },
    { 
      id: 'ABS', 
      name: 'ABS', 
      subtitle: 'Standard',
      color: '#64748b',
      gradient: 'from-slate-400 to-slate-500',
      bestFor: 'General Mechanical Parts',
      thumbnail: 'https://customer-assets.emergentagent.com/job_be0e1b02-3a9b-4b4f-bc88-46ae16a80066/artifacts/xnbkpo1t_ABS%201.jpg'
    },
    { 
      id: 'ABS+', 
      name: 'ABS+', 
      subtitle: 'Reinforced',
      color: '#475569',
      gradient: 'from-slate-500 to-slate-600',
      bestFor: 'General Mechanical Parts',
      thumbnail: 'https://customer-assets.emergentagent.com/job_be0e1b02-3a9b-4b4f-bc88-46ae16a80066/artifacts/xnbkpo1t_ABS%201.jpg'
    },
    { 
      id: 'ASA', 
      name: 'ASA', 
      subtitle: 'UV Resistant',
      color: '#f59e0b',
      gradient: 'from-amber-400 to-amber-500',
      bestFor: 'Mechanical and Functional Parts',
      thumbnail: 'https://customer-assets.emergentagent.com/job_be0e1b02-3a9b-4b4f-bc88-46ae16a80066/artifacts/bam19z9v_ASA%201.jpg'
    },
    { 
      id: 'TPU 95A', 
      name: 'TPU 95A', 
      subtitle: 'Standard Flexible',
      color: '#f97316',
      gradient: 'from-orange-400 to-orange-500',
      bestFor: 'Bushings, joints',
      thumbnail: 'https://customer-assets.emergentagent.com/job_be0e1b02-3a9b-4b4f-bc88-46ae16a80066/artifacts/mww67tru_TPU%201.jpg'
    },
    { 
      id: 'TPU 64D', 
      name: 'TPU 64D', 
      subtitle: 'Semi-rigid',
      color: '#ea580c',
      gradient: 'from-orange-500 to-orange-600',
      bestFor: 'Gaskets, grips',
      thumbnail: 'https://customer-assets.emergentagent.com/job_be0e1b02-3a9b-4b4f-bc88-46ae16a80066/artifacts/rc2v4n43_TPU%2064D%201.jpg'
    },
    { 
      id: 'TPU 85A', 
      name: 'TPU 85A', 
      subtitle: 'Soft Flexible',
      color: '#c2410c',
      gradient: 'from-orange-600 to-orange-700',
      bestFor: 'Soft grips, seals',
      thumbnail: 'https://customer-assets.emergentagent.com/job_be0e1b02-3a9b-4b4f-bc88-46ae16a80066/artifacts/n83wzixq_TPU%2085A%201.jpg'
    },
  ];

  // If the admin has curated a catalog, use that as the source of truth.
  // Otherwise fall back to the hardcoded list above.
  const resolveThumbUrl = (url) => {
    if (!url) return '';
    return url.startsWith('http') ? url : `${BACKEND_URL}${url}`;
  };
  const effectiveMaterials =
    (catalog && catalog.length > 0)
      ? catalog.map((c) => {
          const fallback = materials.find((m) => m.id === c.material_id);
          return {
            id: c.material_id,
            name: c.name || c.material_id,
            subtitle: c.subtitle || fallback?.subtitle || '',
            bestFor: c.best_for || fallback?.best_for || '',
            thumbnail: resolveThumbUrl(c.thumbnail_url) || fallback?.thumbnail || '',
            color: c.color || fallback?.color || '#0007b5',
            gradient: c.gradient || fallback?.gradient || 'from-slate-400 to-slate-500',
          };
        })
      : materials;

  // Merge inventory flags onto the effective material catalog.
  // All tracked materials stay in the list — unavailable ones are rendered
  // disabled with an "Out of Stock" pill. "More Coming" items remain
  // selectable and show an amber pill + restock ETA.
  const inventoryMap = new Map(
    (inventory || []).map((entry) => [entry.material_id, entry])
  );
  const visibleMaterials = effectiveMaterials.map((m) => {
    const inv = inventoryMap.get(m.id);
    if (!inv) return { ...m, isAvailable: true, isMoreComing: false };
    const isAvailable = Boolean(inv.available);
    const isMoreComing = !isAvailable && Boolean(inv.more_coming);
    return {
      ...m,
      isAvailable,
      isMoreComing,
      isOutOfStock: !isAvailable && !isMoreComing,
      restockWeeks: inv.restock_weeks ?? null,
    };
  });

  const selectedMaterial = visibleMaterials.find(m => m.id === value);
  
  return (
    <div className="relative mt-2">
      {/* Trigger Button */}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={`w-full flex items-center justify-between px-4 py-3 bg-white border-2 rounded-xl transition-all duration-300 text-left group ${
          isOpen ? 'border-[#0007b5] shadow-lg shadow-blue-500/10' : 'border-slate-200 hover:border-slate-300'
        }`}
      >
        {selectedMaterial ? (
          <div className="flex items-center gap-3">
            {selectedMaterial.thumbnail ? (
              <img 
                src={selectedMaterial.thumbnail} 
                alt={selectedMaterial.name}
                className="w-10 h-10 rounded-xl object-cover shadow-lg transition-transform duration-300 group-hover:scale-110"
              />
            ) : (
              <div 
                className={`w-10 h-10 rounded-xl bg-gradient-to-br ${selectedMaterial.gradient} shadow-lg transition-transform duration-300 group-hover:scale-110`}
                style={{ boxShadow: `0 4px 14px ${selectedMaterial.color}40` }}
              ></div>
            )}
            <div>
              <span className="font-semibold text-slate-900">{selectedMaterial.name}</span>
              <span className="text-slate-400 ml-2 text-sm">{selectedMaterial.subtitle}</span>
            </div>
          </div>
        ) : (
          <span className="text-slate-400">Choose material</span>
        )}
        <div className={`w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center transition-all duration-300 ${isOpen ? 'bg-[#0007b5] rotate-180' : 'group-hover:bg-slate-200'}`}>
          <svg className={`w-4 h-4 transition-all duration-300 ${isOpen ? 'text-white rotate-90' : 'text-slate-400'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </div>
      </button>
      
      {/* Expandable Panel with Fluid Animation */}
      <div 
        className={`overflow-hidden transition-all duration-500 ease-out ${
          isOpen ? 'max-h-[700px] opacity-100 mt-3' : 'max-h-0 opacity-0 mt-0'
        }`}
      >
        <div className="bg-white rounded-2xl border-2 border-slate-100 shadow-xl overflow-hidden">
          {/* Header */}
          <div className="px-5 py-4 bg-gradient-to-r from-slate-50 to-white border-b border-slate-100">
            <div className="flex items-center justify-between">
              <div>
                <h4 className="font-bold text-slate-900">Select Material</h4>
                <p className="text-xs text-slate-500 mt-0.5">Choose the best material for your project</p>
              </div>
              <a href="/material-guide" className="text-xs text-[#0007b5] hover:underline flex items-center gap-1 font-medium">
                <FileText className="w-3 h-3" />
                Full Guide
              </a>
            </div>
          </div>
          
          {/* Material List - Stylized */}
          <div className="p-3 space-y-2 max-h-[500px] overflow-y-auto">
            {visibleMaterials.map((material, index) => {
              const isSelected = value === material.id;
              const isHovered = hoveredId === material.id;
              const isDisabled = material.isOutOfStock;
              return (
                <button
                  key={material.id}
                  type="button"
                  disabled={isDisabled}
                  onClick={() => {
                    if (isDisabled) return;
                    onChange(material.id);
                    setIsOpen(false);
                  }}
                  onMouseEnter={() => setHoveredId(material.id)}
                  onMouseLeave={() => setHoveredId(null)}
                  className={`w-full flex items-center gap-4 p-3 rounded-xl transition-all duration-300 text-left group relative ${
                    isDisabled
                      ? 'bg-slate-50 cursor-not-allowed opacity-60'
                      : isSelected
                        ? 'bg-[#0007b5] shadow-lg'
                        : isHovered
                          ? 'bg-slate-50'
                          : 'bg-white hover:bg-slate-50'
                  }`}
                  style={{
                    transitionDelay: `${index * 30}ms`,
                    transform: isOpen ? 'translateX(0)' : 'translateX(-20px)',
                    opacity: isOpen ? (isDisabled ? 0.6 : 1) : 0,
                  }}
                  data-testid={`material-option-${material.id}`}
                >
                  {/* Color Swatch or Thumbnail with Glow Effect */}
                  <div className="relative flex-shrink-0">
                    {material.thumbnail ? (
                      <img 
                        src={material.thumbnail} 
                        alt={material.name}
                        className={`w-16 h-16 sm:w-20 sm:h-20 md:w-24 md:h-24 rounded-xl object-contain bg-slate-50 shadow-lg transition-all duration-300 ${isHovered || isSelected ? 'scale-105' : ''} ${isDisabled ? 'grayscale' : ''}`}
                        style={{ 
                          boxShadow: isHovered || isSelected ? `0 8px 24px ${material.color}50` : `0 4px 12px ${material.color}30`
                        }}
                      />
                    ) : (
                      <div 
                        className={`w-16 h-16 sm:w-20 sm:h-20 md:w-24 md:h-24 rounded-xl bg-gradient-to-br ${material.gradient} shadow-lg transition-all duration-300 ${isHovered || isSelected ? 'scale-105' : ''} ${isDisabled ? 'grayscale' : ''}`}
                        style={{ 
                          boxShadow: isHovered || isSelected ? `0 8px 24px ${material.color}50` : `0 4px 12px ${material.color}30`
                        }}
                      ></div>
                    )}
                    {isSelected && !isDisabled && (
                      <div className="absolute -top-1 -right-1 w-5 h-5 sm:w-6 sm:h-6 bg-white rounded-full flex items-center justify-center shadow-md">
                        <Check className="w-3 h-3 sm:w-4 sm:h-4 text-[#0007b5]" />
                      </div>
                    )}
                  </div>
                  
                  {/* Material Info */}
                  <div className="flex-1 min-w-0 flex flex-col justify-center">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`font-semibold transition-colors duration-300 ${isSelected && !isDisabled ? 'text-white' : 'text-slate-900'}`}>
                        {material.name}
                      </span>
                      {material.isMoreComing && (
                        <span
                          className={`inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded ${
                            isSelected ? 'bg-amber-300 text-amber-900' : 'bg-amber-100 text-amber-800'
                          }`}
                          data-testid={`material-more-coming-badge-${material.id}`}
                        >
                          More Coming
                        </span>
                      )}
                      {material.isOutOfStock && (
                        <span
                          className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded bg-red-100 text-red-700"
                          data-testid={`material-out-of-stock-badge-${material.id}`}
                        >
                          Out of Stock
                        </span>
                      )}
                    </div>
                    <div className={`text-sm transition-colors duration-300 ${isSelected && !isDisabled ? 'text-blue-200' : 'text-slate-400'}`}>
                      {material.subtitle}
                    </div>
                    <div className={`mt-1 transition-all duration-300 ${isHovered || isSelected ? 'opacity-100' : 'opacity-70'}`}>
                      <span className={`text-[10px] uppercase tracking-wide ${isSelected && !isDisabled ? 'text-blue-200' : 'text-slate-400'}`}>Best for</span>
                      <p className={`text-xs font-medium ${isSelected && !isDisabled ? 'text-white' : 'text-slate-600'}`}>{material.bestFor}</p>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

// Styled Dropdown Selector - Same design as Material selector
const StyledDropdownSelector = ({ value, onChange, options, placeholder, title, subtitle, showGuideLink = false, guideLink = "#", guideLinkText = "Learn More" }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [hoveredId, setHoveredId] = useState(null);
  
  const selectedOption = options.find(o => o.value === value);
  
  return (
    <div className="relative mt-2">
      {/* Trigger Button */}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={`w-full flex items-center justify-between px-4 py-3 bg-white border-2 rounded-xl transition-all duration-300 text-left group ${
          isOpen ? 'border-[#0007b5] shadow-lg shadow-blue-500/10' : 'border-slate-200 hover:border-slate-300'
        }`}
      >
        {selectedOption ? (
          <div className="flex items-center gap-3">
            {selectedOption.color && (
              <div 
                className={`w-8 h-8 rounded-lg bg-gradient-to-br ${selectedOption.gradient || 'from-slate-400 to-slate-500'} shadow-md transition-transform duration-300 group-hover:scale-110`}
                style={{ backgroundColor: selectedOption.color }}
              ></div>
            )}
            <div>
              <span className="font-semibold text-slate-900">{selectedOption.label}</span>
              {selectedOption.subtitle && (
                <span className="text-slate-400 ml-2 text-sm">{selectedOption.subtitle}</span>
              )}
            </div>
          </div>
        ) : (
          <span className="text-slate-400">{placeholder || 'Select an option'}</span>
        )}
        <div className={`w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center transition-all duration-300 ${isOpen ? 'bg-[#0007b5] rotate-180' : 'group-hover:bg-slate-200'}`}>
          <svg className={`w-4 h-4 transition-all duration-300 ${isOpen ? 'text-white rotate-90' : 'text-slate-400'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </div>
      </button>
      
      {/* Expandable Panel with Fluid Animation */}
      <div 
        className={`overflow-hidden transition-all duration-500 ease-out ${
          isOpen ? 'max-h-[400px] opacity-100 mt-3' : 'max-h-0 opacity-0 mt-0'
        }`}
      >
        <div className="bg-white rounded-2xl border-2 border-slate-100 shadow-xl overflow-hidden">
          {/* Header */}
          {(title || showGuideLink) && (
            <div className="px-5 py-4 bg-gradient-to-r from-slate-50 to-white border-b border-slate-100">
              <div className="flex items-center justify-between">
                <div>
                  {title && <h4 className="font-bold text-slate-900">{title}</h4>}
                  {subtitle && <p className="text-xs text-slate-500 mt-0.5">{subtitle}</p>}
                </div>
                {showGuideLink && (
                  <a href={guideLink} className="text-xs text-[#0007b5] hover:underline flex items-center gap-1 font-medium">
                    <FileText className="w-3 h-3" />
                    {guideLinkText}
                  </a>
                )}
              </div>
            </div>
          )}
          
          {/* Options List */}
          <div className="p-3 space-y-1 max-h-[300px] overflow-y-auto">
            {options.map((option, index) => {
              const isSelected = value === option.value;
              const isHovered = hoveredId === option.value;
              return (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => {
                    onChange(option.value);
                    setIsOpen(false);
                  }}
                  onMouseEnter={() => setHoveredId(option.value)}
                  onMouseLeave={() => setHoveredId(null)}
                  className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-300 text-left ${
                    isSelected 
                      ? 'bg-[#0007b5] shadow-lg' 
                      : isHovered
                        ? 'bg-slate-50'
                        : 'bg-white hover:bg-slate-50'
                  }`}
                  style={{
                    transitionDelay: `${index * 20}ms`,
                    transform: isOpen ? 'translateX(0)' : 'translateX(-10px)',
                    opacity: isOpen ? 1 : 0,
                  }}
                >
                  {option.color && (
                    <div 
                      className={`w-6 h-6 rounded-lg shadow-sm transition-all duration-300 ${isHovered || isSelected ? 'scale-110' : ''}`}
                      style={{ 
                        backgroundColor: option.color,
                        boxShadow: isHovered || isSelected ? `0 4px 12px ${option.color}50` : `0 2px 6px ${option.color}30`
                      }}
                    ></div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className={`font-medium transition-colors duration-300 ${isSelected ? 'text-white' : 'text-slate-900'}`}>
                      {option.label}
                    </div>
                    {option.subtitle && (
                      <div className={`text-xs transition-colors duration-300 ${isSelected ? 'text-blue-200' : 'text-slate-400'}`}>
                        {option.subtitle}
                      </div>
                    )}
                  </div>
                  {isSelected && (
                    <Check className="w-5 h-5 text-white flex-shrink-0" />
                  )}
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

// 2D to 3D Conversion Wizard (5-step wizard matching Custom Design structure)
const ConversionServiceWizard = ({ onSubmit, onBackToServices, onDirtyChange }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [direction, setDirection] = useState(1);
  const [legendOpen, setLegendOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submittedResult, setSubmittedResult] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    companyName: "",
    phone: "",
    description: "",
    drawingDimensions: "",
    drawingScale: "",
    drawingScaleCustom: "",
    files: [],
    application: "",
    applicationOther: "",
    configuration: "",
    printingRequirements: "",
    fitmentRequirements: "",
    timeline: "",
    timelineOther: "",
    optionalServices: [],
    shippingDestination: "",
    postalCode: ""
  });

  // Emit a dirty signal to the parent whenever the user has touched any field.
  // Parent uses this to decide whether to show the "Switch Service?" warning.
  useEffect(() => {
    if (!onDirtyChange) return;
    const d = !!(
      formData.files.length > 0 ||
      formData.name ||
      formData.email ||
      formData.companyName ||
      formData.phone ||
      formData.description ||
      formData.drawingDimensions ||
      formData.drawingScale ||
      formData.drawingScaleCustom ||
      formData.application ||
      formData.applicationOther ||
      formData.configuration ||
      formData.printingRequirements ||
      formData.fitmentRequirements ||
      formData.timeline ||
      formData.timelineOther ||
      (formData.optionalServices && formData.optionalServices.length > 0) ||
      formData.shippingDestination ||
      formData.postalCode
    );
    onDirtyChange(d);
  }, [formData, onDirtyChange]);

  const handleOptionalServiceChange = (service) => {
    setFormData(prev => ({
      ...prev,
      optionalServices: prev.optionalServices.includes(service)
        ? prev.optionalServices.filter(s => s !== service)
        : [...prev.optionalServices, service]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData.files.length === 0) {
      alert('File upload is required for 2D to 3D Conversion');
      return;
    }
    const submitData = {
      ...formData,
      file: formData.files[0],
      application: formData.application === 'other' ? formData.applicationOther : formData.application,
      timeline: formData.timeline === 'other' ? formData.timelineOther : formData.timeline
    };

    const snapshot = {
      name: formData.name,
      email: formData.email,
      companyName: formData.companyName,
      phone: formData.phone,
      description: formData.description,
      drawingDimensions: formData.drawingDimensions,
      drawingScale: formData.drawingScale === 'custom' ? formData.drawingScaleCustom : formData.drawingScale,
      application: submitData.application,
      configuration: formData.configuration,
      printingRequirements: formData.printingRequirements,
      fitmentRequirements: formData.fitmentRequirements,
      timeline: submitData.timeline,
      optionalServices: [...formData.optionalServices],
      shippingDestination: formData.shippingDestination,
      postalCode: formData.postalCode,
      fileCount: formData.files.length
    };

    setSubmitting(true);
    try {
      const result = await onSubmit("2D to 3D Conversion", submitData, formData.files[0], formData.files.slice(1));
      if (result && result.ok) {
        setSubmittedResult({ data: result.data, snapshot });
        window.scrollTo({ top: 0, behavior: 'smooth' });
        setFormData({
          name: "", email: "", companyName: "", phone: "", description: "",
          drawingDimensions: "",
          drawingScale: "", drawingScaleCustom: "",
          files: [], application: "", applicationOther: "", configuration: "",
          printingRequirements: "", fitmentRequirements: "",
          timeline: "", timelineOther: "", optionalServices: [],
          shippingDestination: "", postalCode: ""
        });
        setCurrentStep(0);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const goToStep = (step) => {
    setDirection(step > currentStep ? 1 : -1);
    setCurrentStep(step);
    setLegendOpen(false);
  };

  const nextStep = () => {
    setDirection(1);
    setCurrentStep(prev => Math.min(prev + 1, 4));
  };

  const prevStep = () => {
    setDirection(-1);
    setCurrentStep(prev => Math.max(prev - 1, 0));
  };

  const isConvertStepCompleted = (stepIndex) => {
    if (stepIndex === 0) return !!formData.description;
    if (stepIndex === 1) {
      if (!formData.application || !formData.configuration) return false;
      if (formData.application === 'other' && !formData.applicationOther) return false;
      return true;
    }
    if (stepIndex === 2) return !!formData.printingRequirements && !!formData.fitmentRequirements;
    if (stepIndex === 3) {
      if (!formData.timeline) return false;
      if (formData.timeline === 'other' && !formData.timelineOther) return false;
      return true;
    }
    if (stepIndex === 4) return !!formData.name && !!formData.email;
    return false;
  };

  const allStepsCompleted = isConvertStepCompleted(0) && isConvertStepCompleted(1) && isConvertStepCompleted(2) && isConvertStepCompleted(3) && isConvertStepCompleted(4) && formData.files.length > 0;

  const applicationOptions = [
    { value: "auto-interior", label: "Auto Interior" },
    { value: "auto-suspension", label: "Auto Suspension" },
    { value: "auto-engine-bay", label: "Auto Engine Bay" },
    { value: "drone-chassis", label: "Drone Chassis" },
    { value: "drone-accessories", label: "Drone Accessories" },
    { value: "organization", label: "Organization" },
    { value: "decorative", label: "Decorative" },
    { value: "storage", label: "Storage" },
    { value: "sim-racing", label: "Sim Racing" },
    { value: "flight-sim", label: "Flight Sim" },
    { value: "manufacturing-jig", label: "Manufacturing Jig" },
    { value: "assembly-jig", label: "Assembly Jig" },
    { value: "other", label: "Other" }
  ];

  const configurationOptions = [
    { value: "individual", label: "Individual piece" },
    { value: "assembly", label: "Assembly" },
    { value: "not-sure", label: "Not sure" }
  ];

  const printingRequirementsOptions = [
    { value: "none", label: "None" },
    { value: "prototyping-only", label: "Prototyping only" },
    { value: "prototyping-and-production", label: "Prototyping and final production" }
  ];

  const fitmentOptions = [
    { value: "no-fitment", label: "No fitment required with other parts" },
    { value: "tolerance-on-drawings", label: "Tolerance indicated on drawings" },
    { value: "coordinated-by-c3d", label: "I need fitment coordinated by C3D Works" }
  ];

  const timelineOptions = [
    { value: "2-4-days", label: "2-4 days" },
    { value: "1-2-weeks", label: "1-2 weeks" },
    { value: "1-month-plus", label: "1 month or more" },
    { value: "other", label: "Other - please specify" }
  ];

  const optionalServicesOptions = [
    { value: "technical-drawings", label: "Technical drawings" },
    { value: "renders", label: "Renders" },
    { value: "production-parts", label: "Production of final parts" }
  ];

  const steps = [
    { title: "Project Details", icon: FileText },
    { title: "Application", icon: Wrench },
    { title: "Requirements", icon: RulerDimensionLine },
    { title: "Timeline", icon: Clock },
    { title: "Contact", icon: User }
  ];

  const totalSteps = steps.length;
  const progress = ((currentStep + 1) / totalSteps) * 100;

  const labelFor = (opts, value) => {
    const match = opts.find(o => o.value === value);
    return match ? match.label : value;
  };

  if (submittedResult) {
    const s = submittedResult.snapshot;
    const requestId = submittedResult.data?.request_id;
    return (
      <ServiceRequestSuccessCard
        serviceLabel="2D to 3D Conversion"
        breadcrumbLabel="2D to 3D"
        snapshot={s}
        requestId={requestId}
        onBackToServices={onBackToServices}
        onSubmitAnother={() => setSubmittedResult(null)}
        rows={[
          { label: "Name", value: s.name },
          { label: "Company", value: s.companyName },
          { label: "Phone", value: s.phone },
          { label: "Drawing dimensions", value: s.drawingDimensions },
          { label: "Drawing scale", value: s.drawingScale },
          { label: "Application", value: labelFor(applicationOptions, s.application) },
          { label: "Configuration", value: labelFor(configurationOptions, s.configuration) },
          { label: "3D printing", value: labelFor(printingRequirementsOptions, s.printingRequirements) },
          { label: "Fitment", value: labelFor(fitmentOptions, s.fitmentRequirements) },
          { label: "Timeline", value: labelFor(timelineOptions, s.timeline) },
          {
            label: "Optional services",
            value: s.optionalServices && s.optionalServices.length > 0
              ? s.optionalServices.map(v => labelFor(optionalServicesOptions, v)).join(', ')
              : null
          },
          {
            label: "Shipping",
            value: s.shippingDestination
              ? `${s.shippingDestination}${s.postalCode ? ` · ${s.postalCode}` : ''}`
              : null
          },
          {
            label: "Attachments",
            value: typeof s.fileCount === 'number' && s.fileCount > 0
              ? `${s.fileCount} file${s.fileCount === 1 ? '' : 's'}`
              : null
          }
        ]}
      />
    );
  }


  return (
    <>
      {/* Breadcrumb Banner */}
      {onBackToServices && (
        <div className="bg-gradient-to-r from-slate-50 to-slate-100/80 px-6 md:px-8 py-3 border-b border-slate-200">
          <div className="flex items-center gap-3">
            <button
              onClick={onBackToServices}
              className="inline-flex items-center gap-2 text-[#0007b5] hover:text-[#0015e6] font-semibold text-sm transition-colors group"
            >
              <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
              All Services
            </button>
            <div className="w-0 h-0 border-t-[5px] border-t-transparent border-b-[5px] border-b-transparent border-l-[6px] border-l-slate-300" />
            <span className="text-slate-700 font-semibold text-sm">2D to 3D Conversion</span>
          </div>
        </div>
      )}

      {/* Service Header */}
      <div className="px-6 md:px-8 py-4 border-b border-slate-100">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#0007b5] to-blue-600 flex items-center justify-center shadow-lg">
              <Layers className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="text-xl md:text-2xl font-bold text-slate-900">2D to 3D Conversion</h2>
              <p className="text-sm text-slate-500">Convert your 2D drawings into printable 3D models</p>
            </div>
          </div>
          <div className="text-sm text-slate-500 font-medium hidden sm:block">
            Step {currentStep + 1} of {totalSteps}
          </div>
        </div>
        <div className="mt-4 h-1.5 bg-slate-200 rounded-full overflow-hidden">
          <div
            className="h-full bg-gradient-to-r from-[#0007b5] to-blue-500 transition-all duration-500 ease-out"
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>

      <div className="flex flex-col lg:flex-row">
        {/* Sidebar */}
        <div className={`
          ${legendOpen ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0 lg:max-h-none lg:opacity-100'}
          lg:w-72 lg:min-h-full overflow-hidden transition-all duration-300 ease-out
          bg-gradient-to-b from-[#0007b5] via-[#0010d9] to-blue-600
        `}>
          <div className="p-6">
            <div className="flex items-center justify-between mb-6">
              <span className="text-white/80 text-xs uppercase tracking-wider font-semibold">Steps</span>
              <button
                type="button"
                onClick={() => setLegendOpen(false)}
                className="lg:hidden p-1 rounded hover:bg-white/10 transition-colors"
              >
                <X className="w-4 h-4 text-white/80" />
              </button>
            </div>
            <nav className="space-y-2">
              {steps.map((step, index) => {
                const StepIcon = step.icon;
                const isActive = currentStep === index;
                const isPastStep = currentStep > index;
                const stepCompleted = isConvertStepCompleted(index);
                return (
                  <button
                    key={index}
                    type="button"
                    onClick={() => goToStep(index)}
                    data-testid={`convert-form-step-${index}`}
                    className={`w-full flex items-center gap-3 px-3 py-3 rounded-xl transition-all duration-200 text-left ${
                      isActive
                        ? 'bg-white/20 text-white shadow-lg'
                        : 'text-white/70 hover:bg-white/10 hover:text-white'
                    }`}
                  >
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
                      isActive ? 'bg-white text-[#0007b5]' :
                      isPastStep && stepCompleted ? 'bg-emerald-500 text-white' :
                      isPastStep && !stepCompleted ? 'bg-amber-500 text-white' :
                      'bg-white/10 text-white/60'
                    }`}>
                      {isPastStep ? (
                        stepCompleted ? <Check className="w-4 h-4" /> : <AlertTriangle className="w-4 h-4" />
                      ) : (
                        <StepIcon className="w-4 h-4" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className={`text-sm font-medium truncate ${isActive ? 'text-white' : ''}`}>
                        {step.title}
                      </p>
                      <p className="text-xs text-white/50">Step {index + 1}</p>
                    </div>
                  </button>
                );
              })}
            </nav>
          </div>
        </div>

        {/* Form Content */}
        <div className="flex-1 p-6 md:p-8">
          <form onSubmit={handleSubmit}>
            {/* File Upload Section */}
            <MultiFileUpload
              files={formData.files}
              onFilesChange={(files) => setFormData({...formData, files})}
              fileInputId="convert-service-file"
              accept=".pdf,.dwg,.dxf,.stl,.obj,.3mf,.ply,.amf,.step,.stp,.jpg,.jpeg,.png"
              emptyIcon={Upload}
              emptyTitle="Upload Your 2D File"
              emptySubtitle="Upload your drawing, blueprint, or sketch to convert"
              emptyButtonText="Browse Files"
              emptyNote="Images, PDFs, CAD files, DXF, DWG supported"
              renderPreview={(file) => <FilePreview file={file} />}
            />
            <div className="flex items-start gap-2 -mt-6 mb-8 text-xs text-slate-500">
              <Lock className="w-4 h-4 text-slate-700 flex-shrink-0 mt-0.5" />
              <p><span className="font-bold text-slate-700">You own your ideas and designs.</span> All uploads are confidential and not shared with any third parties.</p>
            </div>

            {/* Animated Step Content */}
            <div className="relative overflow-hidden min-h-[280px] px-1 pb-1 pb-1">
              {/* Step 0: Project Details */}
              <div
                className={`transition-all duration-300 ease-out ${
                  currentStep === 0
                    ? 'opacity-100 translate-x-0'
                    : direction > 0
                      ? 'opacity-0 -translate-x-full absolute inset-0'
                      : 'opacity-0 translate-x-full absolute inset-0'
                }`}
              >
                <h3 className="text-lg font-semibold text-slate-900 mb-6">Project Details</h3>
                <div>
                  <Label className="text-base font-medium">Describe your sketch(es) or drawing(s)</Label>
                  <textarea
                    placeholder="Tell us about the part/component in your drawing or sketch and any specific requirements..."
                    value={formData.description}
                    onChange={(e) => setFormData({...formData, description: e.target.value})}
                    required
                    rows={7}
                    className="w-full mt-2 px-4 py-4 bg-white border-2 border-slate-200 rounded-xl focus:border-[#0007b5] focus:outline-none resize-none"
                  />
                </div>
                <div className="mt-6">
                  <Label className="text-base font-medium">Sketch/Drawing Dimensions</Label>
                  <StyledSelector
                    value={formData.drawingDimensions}
                    onChange={(v) => setFormData({...formData, drawingDimensions: v, drawingScale: "", drawingScaleCustom: ""})}
                    placeholder="Select dimension type"
                    options={[
                      { id: 'nts', name: 'Not to Scale (NTS)' },
                      { id: 'metric', name: 'Metric' },
                      { id: 'standard', name: 'Standard' },
                      { id: 'not-sure', name: 'Not Sure' },
                    ]}
                  />
                  {(formData.drawingDimensions === 'metric' || formData.drawingDimensions === 'standard') && (
                    <div className="mt-4">
                      <Label className="text-base font-medium">Scale</Label>
                      <StyledSelector
                        value={formData.drawingScale}
                        onChange={(v) => setFormData({...formData, drawingScale: v, drawingScaleCustom: ""})}
                        placeholder="Select scale"
                        options={[
                          { id: 'title-block', name: 'Indicated on Title Block' },
                          { id: 'enter-scale', name: 'Enter Scale' },
                        ]}
                      />
                      {formData.drawingScale === 'enter-scale' && (
                        <input
                          type="text"
                          placeholder={formData.drawingDimensions === 'standard'
                            ? 'e.g. 1"=1\', 1/2"=1\', 1/4"=1\', 3/8"=1\''
                            : 'e.g. 1:2, 2:1, 1:10'}
                          value={formData.drawingScaleCustom}
                          onChange={(e) => setFormData({...formData, drawingScaleCustom: e.target.value})}
                          className="w-full mt-2 px-4 py-4 bg-white border-2 border-slate-200 rounded-xl focus:border-[#0007b5] focus:outline-none"
                        />
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Step 1: Application & Configuration */}
              <div
                className={`transition-all duration-300 ease-out ${
                  currentStep === 1
                    ? 'opacity-100 translate-x-0'
                    : currentStep > 1
                      ? 'opacity-0 -translate-x-full absolute inset-0'
                      : 'opacity-0 translate-x-full absolute inset-0'
                }`}
              >
                <h3 className="text-lg font-semibold text-slate-900 mb-6">Application & Configuration</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <Label className="text-base font-medium">Application</Label>
                    <StyledSelector
                      value={formData.application}
                      onChange={(v) => setFormData({...formData, application: v})}
                      placeholder="Select application"
                      options={applicationOptions.map(o => ({ id: o.value, name: o.label }))}
                    />
                    {formData.application === 'other' && (
                      <input
                        type="text"
                        placeholder="Please specify..."
                        value={formData.applicationOther}
                        onChange={(e) => setFormData({...formData, applicationOther: e.target.value})}
                        className="w-full mt-2 px-4 py-4 bg-white border-2 border-slate-200 rounded-xl focus:border-[#0007b5] focus:outline-none"
                      />
                    )}
                  </div>
                  <div>
                    <Label className="text-base font-medium">Configuration</Label>
                    <StyledSelector
                      value={formData.configuration}
                      onChange={(v) => setFormData({...formData, configuration: v})}
                      placeholder="Select configuration"
                      options={configurationOptions.map(o => ({ id: o.value, name: o.label }))}
                    />
                  </div>
                </div>
              </div>

              {/* Step 2: Requirements */}
              <div
                className={`transition-all duration-300 ease-out ${
                  currentStep === 2
                    ? 'opacity-100 translate-x-0'
                    : currentStep > 2
                      ? 'opacity-0 -translate-x-full absolute inset-0'
                      : 'opacity-0 translate-x-full absolute inset-0'
                }`}
              >
                <h3 className="text-lg font-semibold text-slate-900 mb-6">Requirements</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <Label className="text-base font-medium">3D Printing Requirements</Label>
                    <StyledSelector
                      value={formData.printingRequirements}
                      onChange={(v) => setFormData({...formData, printingRequirements: v})}
                      placeholder="Select printing requirements"
                      options={printingRequirementsOptions.map(o => ({ id: o.value, name: o.label }))}
                    />
                  </div>
                  <div>
                    <Label className="text-base font-medium">Fitment Requirements</Label>
                    <StyledSelector
                      value={formData.fitmentRequirements}
                      onChange={(v) => setFormData({...formData, fitmentRequirements: v})}
                      placeholder="Select fitment requirements"
                      options={fitmentOptions.map(o => ({ id: o.value, name: o.label }))}
                    />
                  </div>
                </div>
              </div>

              {/* Step 3: Timeline & Services */}
              <div
                className={`transition-all duration-300 ease-out ${
                  currentStep === 3
                    ? 'opacity-100 translate-x-0'
                    : currentStep > 3
                      ? 'opacity-0 -translate-x-full absolute inset-0'
                      : 'opacity-0 translate-x-full absolute inset-0'
                }`}
              >
                <h3 className="text-lg font-semibold text-slate-900 mb-6">Timeline & Services</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <Label className="text-base font-medium">How soon do you need a concept?</Label>
                    <StyledSelector
                      value={formData.timeline}
                      onChange={(v) => setFormData({...formData, timeline: v})}
                      placeholder="Select timeline"
                      options={timelineOptions.map(o => ({ id: o.value, name: o.label }))}
                    />
                    {formData.timeline === 'other' && (
                      <input
                        type="text"
                        placeholder="Please specify timeline..."
                        value={formData.timelineOther}
                        onChange={(e) => setFormData({...formData, timelineOther: e.target.value})}
                        className="w-full mt-2 px-4 py-4 bg-white border-2 border-slate-200 rounded-xl focus:border-[#0007b5] focus:outline-none"
                      />
                    )}
                  </div>
                  <div>
                    <Label className="text-base font-medium">Optional Services</Label>
                    <div className="mt-2 space-y-3">
                      {optionalServicesOptions.map(option => (
                        <label key={option.value} className="flex items-center gap-3 cursor-pointer group">
                          <div className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-all ${
                            formData.optionalServices.includes(option.value)
                              ? 'bg-[#0007b5] border-[#0007b5]'
                              : 'border-slate-300 group-hover:border-[#0007b5]'
                          }`}>
                            {formData.optionalServices.includes(option.value) && (
                              <Check className="w-3 h-3 text-white" />
                            )}
                          </div>
                          <input
                            type="checkbox"
                            checked={formData.optionalServices.includes(option.value)}
                            onChange={() => handleOptionalServiceChange(option.value)}
                            className="hidden"
                          />
                          <span className="text-slate-700">{option.label}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* Step 4: Contact Info */}
              <div
                className={`transition-all duration-300 ease-out ${
                  currentStep === 4
                    ? 'opacity-100 translate-x-0'
                    : 'opacity-0 translate-x-full absolute inset-0'
                }`}
              >
                <h3 className="text-lg font-semibold text-slate-900 mb-6">Contact Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <Label className="text-base font-medium">Name</Label>
                    <input
                      type="text"
                      placeholder="Your name"
                      value={formData.name}
                      onChange={(e) => setFormData({...formData, name: e.target.value})}
                      required
                      className="w-full mt-2 px-4 py-4 bg-white border-2 border-slate-200 rounded-xl focus:border-[#0007b5] focus:outline-none"
                    />
                  </div>
                  <div>
                    <Label className="text-base font-medium">Email</Label>
                    <input
                      type="email"
                      placeholder="you@example.com"
                      value={formData.email}
                      onChange={(e) => setFormData({...formData, email: e.target.value})}
                      required
                      className="w-full mt-2 px-4 py-4 bg-white border-2 border-slate-200 rounded-xl focus:border-[#0007b5] focus:outline-none"
                    />
                  </div>
                  <div>
                    <Label className="text-base font-medium">Company Name <span className="text-sm font-normal text-slate-400">(Optional)</span></Label>
                    <input
                      type="text"
                      placeholder="Your company"
                      value={formData.companyName}
                      onChange={(e) => setFormData({...formData, companyName: e.target.value})}
                      className="w-full mt-2 px-4 py-4 bg-white border-2 border-slate-200 rounded-xl focus:border-[#0007b5] focus:outline-none"
                    />
                  </div>
                  <div>
                    <Label className="text-base font-medium">Phone Number <span className="text-sm font-normal text-slate-400">(Optional)</span></Label>
                    <input
                      type="tel"
                      placeholder="(555) 123-4567"
                      value={formData.phone}
                      onChange={(e) => setFormData({...formData, phone: e.target.value})}
                      className="w-full mt-2 px-4 py-4 bg-white border-2 border-slate-200 rounded-xl focus:border-[#0007b5] focus:outline-none"
                    />
                  </div>
                </div>

                {/* Shipping Details */}
                <div className="mt-8 pt-6 border-t border-slate-200">
                  <h3 className="text-lg font-semibold text-slate-900 mb-1">Shipping Details</h3>
                  <p className="text-sm text-slate-500 mb-6">If required for shipping prototypes or final components.</p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <Label className="text-base font-medium">Shipping Destination</Label>
                      <StyledSelector
                        value={formData.shippingDestination}
                        onChange={(v) => setFormData({...formData, shippingDestination: v})}
                        placeholder="Choose destination"
                        options={[
                          { id: 'CA', name: 'Canada' },
                          { id: 'US', name: 'United States' },
                        ]}
                      />
                    </div>
                    <div>
                      <Label className="text-base font-medium">Postal/Zip Code <span className="text-sm font-normal text-slate-400">(Optional)</span></Label>
                      <input
                        type="text"
                        placeholder={formData.shippingDestination === "CA" ? "A1A 1A1" : "12345"}
                        value={formData.postalCode}
                        onChange={(e) => setFormData({...formData, postalCode: e.target.value})}
                        className="w-full mt-2 px-4 py-4 bg-white border-2 border-slate-200 rounded-xl focus:border-[#0007b5] focus:outline-none"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Navigation */}
            <div className="flex items-center justify-between mt-8 pt-6 border-t border-slate-200">
              <button
                type="button"
                onClick={prevStep}
                className={`flex items-center gap-2 px-4 py-2 text-slate-600 hover:text-slate-900 transition-colors ${currentStep === 0 ? 'invisible' : ''}`}
              >
                <ArrowLeft className="w-4 h-4" />
                Back
              </button>

              {currentStep < totalSteps - 1 ? (
                <button
                  type="button"
                  onClick={nextStep}
                  className="flex items-center gap-2 px-6 py-3 bg-[#0007b5] text-white rounded-xl hover:bg-[#000690] transition-colors font-medium"
                >
                  Next
                  <ArrowRight className="w-4 h-4" />
                </button>
              ) : (
                <button
                  type="submit"
                  disabled={submitting || !allStepsCompleted}
                  className={`flex items-center gap-2 px-6 py-3 rounded-xl font-medium transition-all ${
                    !submitting && allStepsCompleted
                      ? 'bg-[#0007b5] text-white hover:bg-[#000690] shadow-lg shadow-blue-500/20 cursor-pointer'
                      : 'bg-[#0007b5]/40 text-white/70 cursor-not-allowed'
                  }`}
                >
                  {submitting ? 'Submitting…' : 'Submit Request'}
                  <ArrowRight className="w-4 h-4" />
                </button>
              )}
            </div>
          </form>
        </div>
      </div>
    </>
  );
};

// Service Wizard with 3D Preview (for Optimize and Renders tabs) - 5-step wizard
const ServiceWizard3D = ({ title, description, serviceType, onSubmit, onBackToServices, onDirtyChange }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [direction, setDirection] = useState(1);
  const [legendOpen, setLegendOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submittedResult, setSubmittedResult] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    companyName: "",
    phone: "",
    description: "",
    files: [],
    application: "",
    applicationOther: "",
    configuration: "",
    printingRequirements: "",
    fitmentRequirements: "",
    timeline: "",
    timelineOther: "",
    optionalServices: [],
    optionalServicesDetails: "",
    shippingDestination: "",
    postalCode: ""
  });

  // Emit dirty state to parent for the "Switch Service?" modal.
  useEffect(() => {
    if (!onDirtyChange) return;
    const d = !!(
      formData.files.length > 0 ||
      formData.name ||
      formData.email ||
      formData.companyName ||
      formData.phone ||
      formData.description ||
      formData.application ||
      formData.applicationOther ||
      formData.configuration ||
      formData.printingRequirements ||
      formData.fitmentRequirements ||
      formData.timeline ||
      formData.timelineOther ||
      (formData.optionalServices && formData.optionalServices.length > 0) ||
      formData.optionalServicesDetails ||
      formData.shippingDestination ||
      formData.postalCode
    );
    onDirtyChange(d);
  }, [formData, onDirtyChange]);

  const handleOptionalServiceChange = (service) => {
    setFormData(prev => {
      const updated = prev.optionalServices.includes(service)
        ? prev.optionalServices.filter(s => s !== service)
        : [...prev.optionalServices, service];
      return { ...prev, optionalServices: updated, optionalServicesDetails: updated.length === 0 ? "" : prev.optionalServicesDetails };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData.files.length === 0) {
      alert('File upload is required for this service');
      return;
    }
    const submitData = {
      ...formData,
      file: formData.files[0],
      application: formData.application === 'other' ? formData.applicationOther : formData.application,
      timeline: formData.timeline === 'other' ? formData.timelineOther : formData.timeline
    };

    const snapshot = {
      name: formData.name,
      email: formData.email,
      companyName: formData.companyName,
      phone: formData.phone,
      description: formData.description,
      application: submitData.application,
      configuration: formData.configuration,
      printingRequirements: formData.printingRequirements,
      fitmentRequirements: formData.fitmentRequirements,
      timeline: submitData.timeline,
      optionalServices: [...formData.optionalServices],
      optionalServicesDetails: formData.optionalServicesDetails,
      shippingDestination: formData.shippingDestination,
      postalCode: formData.postalCode,
      fileCount: formData.files.length
    };

    setSubmitting(true);
    try {
      const result = await onSubmit(serviceType, submitData, formData.files[0], formData.files.slice(1));
      if (result && result.ok) {
        setSubmittedResult({ data: result.data, snapshot });
        window.scrollTo({ top: 0, behavior: 'smooth' });
        setFormData({
          name: "", email: "", companyName: "", phone: "", description: "", files: [],
          application: "", applicationOther: "", configuration: "",
          printingRequirements: "", fitmentRequirements: "",
          timeline: "", timelineOther: "", optionalServices: [],
          optionalServicesDetails: "",
          shippingDestination: "", postalCode: ""
        });
        setCurrentStep(0);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const goToStep = (step) => {
    setDirection(step > currentStep ? 1 : -1);
    setCurrentStep(step);
    setLegendOpen(false);
  };

  const nextStep = () => {
    setDirection(1);
    setCurrentStep(prev => Math.min(prev + 1, 4));
  };

  const prevStep = () => {
    setDirection(-1);
    setCurrentStep(prev => Math.max(prev - 1, 0));
  };

  const isStepDone = (stepIndex) => {
    if (stepIndex === 0) return !!formData.description;
    if (stepIndex === 1) {
      if (!formData.application || !formData.configuration) return false;
      if (formData.application === 'other' && !formData.applicationOther) return false;
      return true;
    }
    if (stepIndex === 2) return !!formData.printingRequirements && !!formData.fitmentRequirements;
    if (stepIndex === 3) {
      if (!formData.timeline) return false;
      if (formData.timeline === 'other' && !formData.timelineOther) return false;
      return true;
    }
    if (stepIndex === 4) return !!formData.name && !!formData.email;
    return false;
  };

  const allStepsCompleted = isStepDone(0) && isStepDone(1) && isStepDone(2) && isStepDone(3) && isStepDone(4) && formData.files.length > 0;

  const getServiceIcon = () => {
    switch(serviceType) {
      case "Product Optimization": return Settings;
      case "Product Renders": return Eye;
      default: return FileText;
    }
  };
  const ServiceIcon = getServiceIcon();

  const applicationOptions = serviceType === "Product Renders" ? [
    { value: "marketing", label: "Marketing Material" },
    { value: "product-listing", label: "Product Listing" },
    { value: "presentation", label: "Presentation / Pitch" },
    { value: "social-media", label: "Social Media" },
    { value: "documentation", label: "Documentation" },
    { value: "other", label: "Other" }
  ] : [
    { value: "strength-optimization", label: "Strength Optimization" },
    { value: "weight-reduction", label: "Weight Reduction" },
    { value: "print-optimization", label: "Print Optimization" },
    { value: "material-reduction", label: "Material Reduction" },
    { value: "assembly-simplification", label: "Assembly Simplification" },
    { value: "other", label: "Other" }
  ];

  const configurationOptions = [
    { value: "individual", label: "Individual piece" },
    { value: "assembly", label: "Assembly" },
    { value: "not-sure", label: "Not sure" }
  ];

  const printingRequirementsOptions = [
    { value: "prototyping-only", label: "Prototyping only" },
    { value: "prototyping-and-production", label: "Prototyping and final production" }
  ];

  const fitmentOptions = [
    { value: "no-fitment", label: "No fitment required with other parts" },
    { value: "provide-dimensions", label: "I will provide dimensions" },
    { value: "provide-3d-scan", label: "I will provide 3D scan(s)" },
    { value: "coordinated-by-c3d", label: "I need fitment coordinated by C3D Works" }
  ];

  const timelineOptions = [
    { value: "2-4-days", label: "2-4 days" },
    { value: "1-2-weeks", label: "1-2 weeks" },
    { value: "1-month-plus", label: "1 month or more" },
    { value: "other", label: "Other - please specify" }
  ];

  const optionalServicesOptions = serviceType === "Product Renders" ? [
    { value: "ecommerce-product-pictures", label: "E-commerce product pictures" },
    { value: "lifestyle-product-pictures", label: "Lifestyle product pictures" },
    { value: "animations", label: "Animations" }
  ] : [
    { value: "technical-drawings", label: "Technical drawings" },
    { value: "renders", label: "Renders" },
    { value: "production-parts", label: "Production of final parts" }
  ];

  const steps = [
    { title: "Project Details", icon: FileText },
    { title: "Application", icon: Wrench },
    { title: "Requirements", icon: RulerDimensionLine },
    { title: "Timeline", icon: Clock },
    { title: "Contact", icon: User }
  ];

  const totalSteps = steps.length;
  const progress = ((currentStep + 1) / totalSteps) * 100;
  const fileInputId = `3d-file-${serviceType.replace(/\s/g, '-')}`;

  const labelFor = (opts, value) => {
    const match = opts.find(o => o.value === value);
    return match ? match.label : value;
  };

  if (submittedResult) {
    const s = submittedResult.snapshot;
    const requestId = submittedResult.data?.request_id;
    return (
      <ServiceRequestSuccessCard
        serviceLabel={title || serviceType}
        breadcrumbLabel={title || serviceType}
        snapshot={s}
        requestId={requestId}
        onBackToServices={onBackToServices}
        onSubmitAnother={() => setSubmittedResult(null)}
        rows={[
          { label: "Name", value: s.name },
          { label: "Company", value: s.companyName },
          { label: "Phone", value: s.phone },
          { label: "Application", value: labelFor(applicationOptions, s.application) },
          { label: "Configuration", value: labelFor(configurationOptions, s.configuration) },
          { label: "3D printing", value: labelFor(printingRequirementsOptions, s.printingRequirements) },
          { label: "Fitment", value: labelFor(fitmentOptions, s.fitmentRequirements) },
          { label: "Timeline", value: labelFor(timelineOptions, s.timeline) },
          {
            label: "Optional services",
            value: s.optionalServices && s.optionalServices.length > 0
              ? s.optionalServices.map(v => labelFor(optionalServicesOptions, v)).join(', ')
              : null
          },
          { label: "Additional details", value: s.optionalServicesDetails },
          {
            label: "Shipping",
            value: s.shippingDestination
              ? `${s.shippingDestination}${s.postalCode ? ` · ${s.postalCode}` : ''}`
              : null
          },
          {
            label: "Attachments",
            value: typeof s.fileCount === 'number' && s.fileCount > 0
              ? `${s.fileCount} file${s.fileCount === 1 ? '' : 's'}`
              : null
          }
        ]}
      />
    );
  }


  return (
    <>
      {/* Breadcrumb Banner */}
      {onBackToServices && (
        <div className="bg-gradient-to-r from-slate-50 to-slate-100/80 px-6 md:px-8 py-3 border-b border-slate-200">
          <div className="flex items-center gap-3">
            <button
              onClick={onBackToServices}
              className="inline-flex items-center gap-2 text-[#0007b5] hover:text-[#0015e6] font-semibold text-sm transition-colors group"
            >
              <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
              All Services
            </button>
            <div className="w-0 h-0 border-t-[5px] border-t-transparent border-b-[5px] border-b-transparent border-l-[6px] border-l-slate-300" />
            <span className="text-slate-700 font-semibold text-sm">{title}</span>
          </div>
        </div>
      )}

      {/* Service Header */}
      <div className="px-6 md:px-8 py-4 border-b border-slate-100">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#0007b5] to-blue-600 flex items-center justify-center shadow-lg">
              <ServiceIcon className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="text-xl md:text-2xl font-bold text-slate-900">{title}</h2>
              <p className="text-sm text-slate-500">{description}</p>
            </div>
          </div>
          <div className="text-sm text-slate-500 font-medium hidden sm:block">
            Step {currentStep + 1} of {totalSteps}
          </div>
        </div>
        <div className="mt-4 h-1.5 bg-slate-200 rounded-full overflow-hidden">
          <div
            className="h-full bg-gradient-to-r from-[#0007b5] to-blue-500 transition-all duration-500 ease-out"
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>

      <div className="flex flex-col lg:flex-row">
        {/* Sidebar */}
        <div className={`
          ${legendOpen ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0 lg:max-h-none lg:opacity-100'}
          lg:w-72 lg:min-h-full overflow-hidden transition-all duration-300 ease-out
          bg-gradient-to-b from-[#0007b5] via-[#0010d9] to-blue-600
        `}>
          <div className="p-6">
            <div className="flex items-center justify-between mb-6">
              <span className="text-white/80 text-xs uppercase tracking-wider font-semibold">Steps</span>
              <button
                type="button"
                onClick={() => setLegendOpen(false)}
                className="lg:hidden p-1 rounded hover:bg-white/10 transition-colors"
              >
                <X className="w-4 h-4 text-white/80" />
              </button>
            </div>
            <nav className="space-y-2">
              {steps.map((step, index) => {
                const StepIcon = step.icon;
                const isActive = currentStep === index;
                const isPastStep = currentStep > index;
                const stepCompleted = isStepDone(index);
                return (
                  <button
                    key={index}
                    type="button"
                    onClick={() => goToStep(index)}
                    className={`w-full flex items-center gap-3 px-3 py-3 rounded-xl transition-all duration-200 text-left ${
                      isActive
                        ? 'bg-white/20 text-white shadow-lg'
                        : 'text-white/70 hover:bg-white/10 hover:text-white'
                    }`}
                  >
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
                      isActive ? 'bg-white text-[#0007b5]' :
                      isPastStep && stepCompleted ? 'bg-emerald-500 text-white' :
                      isPastStep && !stepCompleted ? 'bg-amber-500 text-white' :
                      'bg-white/10 text-white/60'
                    }`}>
                      {isPastStep ? (
                        stepCompleted ? <Check className="w-4 h-4" /> : <AlertTriangle className="w-4 h-4" />
                      ) : (
                        <StepIcon className="w-4 h-4" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className={`text-sm font-medium truncate ${isActive ? 'text-white' : ''}`}>
                        {step.title}
                      </p>
                      <p className="text-xs text-white/50">Step {index + 1}</p>
                    </div>
                  </button>
                );
              })}
            </nav>
          </div>
        </div>

        {/* Form Content */}
        <div className="flex-1 p-6 md:p-8">
          <form onSubmit={handleSubmit}>
            {/* 3D File Upload Section */}
            <MultiFileUpload
              files={formData.files}
              onFilesChange={(files) => setFormData({...formData, files})}
              fileInputId={fileInputId}
              accept=".stl,.obj,.3mf,.ply,.step,.stp"
              emptyIcon={Rotate3d}
              emptyTitle="Upload your 3D model"
              emptySubtitle="You can view and verify your 3D model here"
              emptyButtonText="Browse 3D Files"
              emptyNote="STL, OBJ, 3MF, STEP, PLY files supported"
              renderPreview={(file) => (
                <div className="w-full min-h-[300px] rounded-xl overflow-hidden bg-gradient-to-b from-slate-200 to-slate-300">
                  <ThreeDPreview
                    file={file}
                    onAnalysis={(analysisData) => console.log('3D Analysis:', analysisData)}
                    onFileModified={(modifiedFile) => {
                      setFormData(prev => {
                        const updated = [...prev.files];
                        const idx = updated.indexOf(file);
                        if (idx >= 0) updated[idx] = modifiedFile;
                        return { ...prev, files: updated };
                      });
                    }}
                  />
                </div>
              )}
            />
            <div className="flex items-start gap-2 -mt-6 mb-8 text-xs text-slate-500">
              <Lock className="w-4 h-4 text-slate-700 flex-shrink-0 mt-0.5" />
              <p><span className="font-bold text-slate-700">You own your ideas and designs.</span> All uploads are confidential and not shared with any third parties.</p>
            </div>

            {/* Animated Step Content */}
            <div className="relative overflow-hidden min-h-[280px] px-1 pb-1">
              {/* Step 0: Project Details */}
              <div className={`transition-all duration-300 ease-out ${
                currentStep === 0 ? 'opacity-100 translate-x-0' : direction > 0 ? 'opacity-0 -translate-x-full absolute inset-0' : 'opacity-0 translate-x-full absolute inset-0'
              }`}>
                <h3 className="text-lg font-semibold text-slate-900 mb-6">Project Details</h3>
                <div>
                  <Label className="text-base font-medium">{serviceType === "Product Renders" ? "Describe your project" : "Describe your model or component(s)"}</Label>
                  <textarea
                    placeholder={serviceType === "Product Renders" ? "Describe the renders you need, angles, backgrounds, etc..." : "Describe what you need optimized and any constraints..."}
                    value={formData.description}
                    onChange={(e) => setFormData({...formData, description: e.target.value})}
                    required
                    rows={7}
                    className="w-full mt-2 px-4 py-4 bg-white border-2 border-slate-200 rounded-xl focus:border-[#0007b5] focus:outline-none resize-none"
                  />
                </div>
              </div>

              {/* Step 1: Application & Configuration */}
              <div className={`transition-all duration-300 ease-out ${
                currentStep === 1 ? 'opacity-100 translate-x-0' : currentStep > 1 ? 'opacity-0 -translate-x-full absolute inset-0' : 'opacity-0 translate-x-full absolute inset-0'
              }`}>
                <h3 className="text-lg font-semibold text-slate-900 mb-6">Application & Configuration</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <Label className="text-base font-medium">{serviceType === "Product Renders" ? "Render Purpose" : "Optimization Goal"}</Label>
                    <StyledSelector
                      value={formData.application}
                      onChange={(v) => setFormData({...formData, application: v})}
                      placeholder={serviceType === "Product Renders" ? "Select render purpose" : "Select optimization goal"}
                      options={applicationOptions.map(o => ({ id: o.value, name: o.label }))}
                    />
                    {formData.application === 'other' && (
                      <input
                        type="text"
                        placeholder="Please specify..."
                        value={formData.applicationOther}
                        onChange={(e) => setFormData({...formData, applicationOther: e.target.value})}
                        className="w-full mt-2 px-4 py-4 bg-white border-2 border-slate-200 rounded-xl focus:border-[#0007b5] focus:outline-none"
                      />
                    )}
                  </div>
                  <div>
                    <Label className="text-base font-medium">Configuration</Label>
                    <StyledSelector
                      value={formData.configuration}
                      onChange={(v) => setFormData({...formData, configuration: v})}
                      placeholder="Select configuration"
                      options={configurationOptions.map(o => ({ id: o.value, name: o.label }))}
                    />
                  </div>
                </div>
              </div>

              {/* Step 2: Requirements */}
              <div className={`transition-all duration-300 ease-out ${
                currentStep === 2 ? 'opacity-100 translate-x-0' : currentStep > 2 ? 'opacity-0 -translate-x-full absolute inset-0' : 'opacity-0 translate-x-full absolute inset-0'
              }`}>
                <h3 className="text-lg font-semibold text-slate-900 mb-6">Requirements</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <Label className="text-base font-medium">3D Printing Requirements</Label>
                    <StyledSelector
                      value={formData.printingRequirements}
                      onChange={(v) => setFormData({...formData, printingRequirements: v})}
                      placeholder="Select printing requirements"
                      options={printingRequirementsOptions.map(o => ({ id: o.value, name: o.label }))}
                    />
                  </div>
                  <div>
                    <Label className="text-base font-medium">Fitment Requirements</Label>
                    <StyledSelector
                      value={formData.fitmentRequirements}
                      onChange={(v) => setFormData({...formData, fitmentRequirements: v})}
                      placeholder="Select fitment requirements"
                      options={fitmentOptions.map(o => ({ id: o.value, name: o.label }))}
                    />
                  </div>
                </div>
              </div>

              {/* Step 3: Timeline & Services */}
              <div className={`transition-all duration-300 ease-out ${
                currentStep === 3 ? 'opacity-100 translate-x-0' : currentStep > 3 ? 'opacity-0 -translate-x-full absolute inset-0' : 'opacity-0 translate-x-full absolute inset-0'
              }`}>
                <h3 className="text-lg font-semibold text-slate-900 mb-6">Timeline & Services</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <Label className="text-base font-medium">How soon do you need this?</Label>
                    <StyledSelector
                      value={formData.timeline}
                      onChange={(v) => setFormData({...formData, timeline: v})}
                      placeholder="Select timeline"
                      options={timelineOptions.map(o => ({ id: o.value, name: o.label }))}
                    />
                    {formData.timeline === 'other' && (
                      <input
                        type="text"
                        placeholder="Please specify timeline..."
                        value={formData.timelineOther}
                        onChange={(e) => setFormData({...formData, timelineOther: e.target.value})}
                        className="w-full mt-2 px-4 py-4 bg-white border-2 border-slate-200 rounded-xl focus:border-[#0007b5] focus:outline-none"
                      />
                    )}
                  </div>
                  <div>
                    <Label className="text-base font-medium">Optional Services</Label>
                    <div className="mt-2 space-y-3">
                      {optionalServicesOptions.map(option => (
                        <label key={option.value} className="flex items-center gap-3 cursor-pointer group">
                          <div className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-all ${
                            formData.optionalServices.includes(option.value)
                              ? 'bg-[#0007b5] border-[#0007b5]'
                              : 'border-slate-300 group-hover:border-[#0007b5]'
                          }`}>
                            {formData.optionalServices.includes(option.value) && (
                              <Check className="w-3 h-3 text-white" />
                            )}
                          </div>
                          <input
                            type="checkbox"
                            checked={formData.optionalServices.includes(option.value)}
                            onChange={() => handleOptionalServiceChange(option.value)}
                            className="hidden"
                          />
                          <span className="text-slate-700">{option.label}</span>
                        </label>
                      ))}
                    </div>
                    {formData.optionalServices.length > 0 && (
                      <textarea
                        placeholder={`Tell us more about what you'd like to see in your ${formData.optionalServices.map(s => { const label = optionalServicesOptions.find(o => o.value === s)?.label; return s === 'animations' ? 'product animations' : label; }).filter(Boolean).join(', ').toLowerCase()}...`}
                        value={formData.optionalServicesDetails}
                        onChange={(e) => setFormData({...formData, optionalServicesDetails: e.target.value})}
                        rows={3}
                        className="w-full mt-4 px-4 py-4 bg-white border-2 border-slate-200 rounded-xl focus:border-[#0007b5] focus:outline-none resize-y"
                      />
                    )}
                  </div>
                </div>
              </div>

              {/* Step 4: Contact Info */}
              <div className={`transition-all duration-300 ease-out ${
                currentStep === 4 ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-full absolute inset-0'
              }`}>
                <h3 className="text-lg font-semibold text-slate-900 mb-6">Contact Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <Label className="text-base font-medium">Name</Label>
                    <input type="text" placeholder="Your name" value={formData.name}
                      onChange={(e) => setFormData({...formData, name: e.target.value})} required
                      className="w-full mt-2 px-4 py-4 bg-white border-2 border-slate-200 rounded-xl focus:border-[#0007b5] focus:outline-none" />
                  </div>
                  <div>
                    <Label className="text-base font-medium">Email</Label>
                    <input type="email" placeholder="you@example.com" value={formData.email}
                      onChange={(e) => setFormData({...formData, email: e.target.value})} required
                      className="w-full mt-2 px-4 py-4 bg-white border-2 border-slate-200 rounded-xl focus:border-[#0007b5] focus:outline-none" />
                  </div>
                  <div>
                    <Label className="text-base font-medium">Company Name <span className="text-sm font-normal text-slate-400">(Optional)</span></Label>
                    <input type="text" placeholder="Your company" value={formData.companyName}
                      onChange={(e) => setFormData({...formData, companyName: e.target.value})}
                      className="w-full mt-2 px-4 py-4 bg-white border-2 border-slate-200 rounded-xl focus:border-[#0007b5] focus:outline-none" />
                  </div>
                  <div>
                    <Label className="text-base font-medium">Phone Number <span className="text-sm font-normal text-slate-400">(Optional)</span></Label>
                    <input type="tel" placeholder="(555) 123-4567" value={formData.phone}
                      onChange={(e) => setFormData({...formData, phone: e.target.value})}
                      className="w-full mt-2 px-4 py-4 bg-white border-2 border-slate-200 rounded-xl focus:border-[#0007b5] focus:outline-none" />
                  </div>
                </div>

                {/* Shipping Details — only relevant for services that may ship physical parts */}
                {serviceType !== "Product Renders" && (
                  <div className="mt-8 pt-6 border-t border-slate-200">
                    <h3 className="text-lg font-semibold text-slate-900 mb-1">Shipping Details</h3>
                    <p className="text-sm text-slate-500 mb-6">If required for shipping prototypes or final components.</p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <Label className="text-base font-medium">Shipping Destination</Label>
                        <StyledSelector
                          value={formData.shippingDestination}
                          onChange={(v) => setFormData({...formData, shippingDestination: v})}
                          placeholder="Choose destination"
                          options={[
                            { id: 'CA', name: 'Canada' },
                            { id: 'US', name: 'United States' },
                          ]}
                        />
                      </div>
                      <div>
                        <Label className="text-base font-medium">Postal/Zip Code <span className="text-sm font-normal text-slate-400">(Optional)</span></Label>
                        <input type="text"
                          placeholder={formData.shippingDestination === "CA" ? "A1A 1A1" : "12345"}
                          value={formData.postalCode}
                          onChange={(e) => setFormData({...formData, postalCode: e.target.value})}
                          className="w-full mt-2 px-4 py-4 bg-white border-2 border-slate-200 rounded-xl focus:border-[#0007b5] focus:outline-none" />
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Navigation */}
            <div className="flex items-center justify-between mt-8 pt-6 border-t border-slate-200">
              <button type="button" onClick={prevStep}
                className={`flex items-center gap-2 px-4 py-2 text-slate-600 hover:text-slate-900 transition-colors ${currentStep === 0 ? 'invisible' : ''}`}>
                <ArrowLeft className="w-4 h-4" /> Back
              </button>

              {currentStep < totalSteps - 1 ? (
                <button type="button" onClick={nextStep}
                  className="flex items-center gap-2 px-6 py-3 bg-[#0007b5] text-white rounded-xl hover:bg-[#000690] transition-colors font-medium">
                  Next <ArrowRight className="w-4 h-4" />
                </button>
              ) : (
                <button type="submit" disabled={submitting || !allStepsCompleted}
                  className={`flex items-center gap-2 px-6 py-3 rounded-xl font-medium transition-all ${
                    !submitting && allStepsCompleted
                      ? 'bg-[#0007b5] text-white hover:bg-[#000690] shadow-lg shadow-blue-500/20 cursor-pointer'
                      : 'bg-[#0007b5]/40 text-white/70 cursor-not-allowed'
                  }`}>
                  {submitting ? 'Submitting…' : 'Submit Request'} <ArrowRight className="w-4 h-4" />
                </button>
              )}
            </div>
          </form>
        </div>
      </div>
    </>
  );
};

// Design Service Wizard with expanded project details (5-step wizard)
const DesignServiceWizard = ({ onSubmit, onBackToServices, onDirtyChange }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [direction, setDirection] = useState(1);
  const [legendOpen, setLegendOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submittedResult, setSubmittedResult] = useState(null); // { data, snapshot }
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    companyName: "",
    phone: "",
    description: "",
    files: [],
    application: "",
    applicationOther: "",
    configuration: "",
    printingRequirements: "",
    fitmentRequirements: "",
    timeline: "",
    timelineOther: "",
    optionalServices: [],
    shippingDestination: "",
    postalCode: ""
  });

  // Emit dirty state to parent for the "Switch Service?" modal.
  useEffect(() => {
    if (!onDirtyChange) return;
    const d = !!(
      (formData.files && formData.files.length > 0) ||
      formData.name ||
      formData.email ||
      formData.companyName ||
      formData.phone ||
      formData.description ||
      formData.application ||
      formData.applicationOther ||
      formData.configuration ||
      formData.printingRequirements ||
      formData.fitmentRequirements ||
      formData.timeline ||
      formData.timelineOther ||
      (formData.optionalServices && formData.optionalServices.length > 0) ||
      formData.shippingDestination ||
      formData.postalCode
    );
    onDirtyChange(d);
  }, [formData, onDirtyChange]);

  const handleOptionalServiceChange = (service) => {
    setFormData(prev => ({
      ...prev,
      optionalServices: prev.optionalServices.includes(service)
        ? prev.optionalServices.filter(s => s !== service)
        : [...prev.optionalServices, service]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const submitData = {
      ...formData,
      file: formData.files[0] || null,
      application: formData.application === 'other' ? formData.applicationOther : formData.application,
      timeline: formData.timeline === 'other' ? formData.timelineOther : formData.timeline
    };

    // Capture a snapshot of the submission for the success page (formData resets after submit)
    const snapshot = {
      name: formData.name,
      email: formData.email,
      companyName: formData.companyName,
      phone: formData.phone,
      description: formData.description,
      application: submitData.application,
      configuration: formData.configuration,
      printingRequirements: formData.printingRequirements,
      fitmentRequirements: formData.fitmentRequirements,
      timeline: submitData.timeline,
      optionalServices: [...formData.optionalServices],
      shippingDestination: formData.shippingDestination,
      postalCode: formData.postalCode,
      fileCount: formData.files.length
    };

    setSubmitting(true);
    try {
      const result = await onSubmit("Design Service", submitData, formData.files[0] || null, formData.files.slice(1));
      if (result && result.ok) {
        setSubmittedResult({ data: result.data, snapshot });
        window.scrollTo({ top: 0, behavior: 'smooth' });
        setFormData({
          name: "", email: "", companyName: "", phone: "", description: "", files: [],
          application: "", applicationOther: "", configuration: "",
          printingRequirements: "", fitmentRequirements: "",
          timeline: "", timelineOther: "", optionalServices: [],
          shippingDestination: "", postalCode: ""
        });
        setCurrentStep(0);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const goToStep = (step) => {
    setDirection(step > currentStep ? 1 : -1);
    setCurrentStep(step);
    setLegendOpen(false);
  };

  const nextStep = () => {
    setDirection(1);
    setCurrentStep(prev => Math.min(prev + 1, 4));
  };

  const prevStep = () => {
    setDirection(-1);
    setCurrentStep(prev => Math.max(prev - 1, 0));
  };

  const isDesignStepCompleted = (stepIndex) => {
    // Step 0: Project Details - description required
    if (stepIndex === 0) return !!formData.description;
    // Step 1: Application - application and configuration required
    if (stepIndex === 1) {
      if (!formData.application || !formData.configuration) return false;
      if (formData.application === 'other' && !formData.applicationOther) return false;
      return true;
    }
    // Step 2: Requirements - printing and fitment required
    if (stepIndex === 2) return !!formData.printingRequirements && !!formData.fitmentRequirements;
    // Step 3: Timeline - timeline required, optional services are optional
    if (stepIndex === 3) {
      if (!formData.timeline) return false;
      if (formData.timeline === 'other' && !formData.timelineOther) return false;
      return true;
    }
    // Step 4: Contact - name and email required
    if (stepIndex === 4) return !!formData.name && !!formData.email;
    return false;
  };

  const applicationOptions = [
    { value: "auto-interior", label: "Auto Interior" },
    { value: "auto-suspension", label: "Auto Suspension" },
    { value: "auto-engine-bay", label: "Auto Engine Bay" },
    { value: "drone-chassis", label: "Drone Chassis" },
    { value: "drone-accessories", label: "Drone Accessories" },
    { value: "organization", label: "Organization" },
    { value: "decorative", label: "Decorative" },
    { value: "storage", label: "Storage" },
    { value: "sim-racing", label: "Sim Racing" },
    { value: "flight-sim", label: "Flight Sim" },
    { value: "manufacturing-jig", label: "Manufacturing Jig" },
    { value: "assembly-jig", label: "Assembly Jig" },
    { value: "other", label: "Other" }
  ];

  const configurationOptions = [
    { value: "individual", label: "Individual piece" },
    { value: "assembly", label: "Assembly" },
    { value: "not-sure", label: "Not sure" }
  ];

  const printingRequirementsOptions = [
    { value: "none", label: "None" },
    { value: "prototyping-only", label: "Prototyping only" },
    { value: "prototyping-and-production", label: "Prototyping and final production" }
  ];

  const fitmentOptions = [
    { value: "no-fitment", label: "No fitment required with other parts" },
    { value: "provide-dimensions", label: "I will provide dimensions" },
    { value: "provide-3d-scan", label: "I will provide 3D scan(s)" },
    { value: "coordinated-by-c3d", label: "I need fitment coordinated by C3D Works" }
  ];

  const timelineOptions = [
    { value: "2-4-days", label: "2-4 days" },
    { value: "1-2-weeks", label: "1-2 weeks" },
    { value: "1-month-plus", label: "1 month or more" },
    { value: "other", label: "Other - please specify" }
  ];

  const optionalServicesOptions = [
    { value: "technical-drawings", label: "Technical drawings" },
    { value: "renders", label: "Renders" },
    { value: "production-parts", label: "Production of final parts" }
  ];

  const steps = [
    { title: "Project Details", icon: FileText },
    { title: "Application", icon: Wrench },
    { title: "Requirements", icon: RulerDimensionLine },
    { title: "Timeline", icon: Clock },
    { title: "Contact", icon: User }
  ];

  const totalSteps = steps.length;
  const progress = ((currentStep + 1) / totalSteps) * 100;

  // Human-readable label helpers for the success summary (use the full options arrays)
  const labelFor = (opts, value) => {
    const match = opts.find(o => o.value === value);
    return match ? match.label : value;
  };

  if (submittedResult) {
    const s = submittedResult.snapshot;
    const requestId = submittedResult.data?.request_id;
    return (
      <ServiceRequestSuccessCard
        serviceLabel="Custom Design"
        breadcrumbLabel="Custom Design"
        snapshot={s}
        requestId={requestId}
        onBackToServices={onBackToServices}
        onSubmitAnother={() => setSubmittedResult(null)}
        rows={[
          { label: "Name", value: s.name },
          { label: "Company", value: s.companyName },
          { label: "Phone", value: s.phone },
          { label: "Application", value: labelFor(applicationOptions, s.application) },
          { label: "Configuration", value: labelFor(configurationOptions, s.configuration) },
          { label: "3D printing", value: labelFor(printingRequirementsOptions, s.printingRequirements) },
          { label: "Fitment", value: labelFor(fitmentOptions, s.fitmentRequirements) },
          { label: "Timeline", value: labelFor(timelineOptions, s.timeline) },
          {
            label: "Optional services",
            value: s.optionalServices && s.optionalServices.length > 0
              ? s.optionalServices.map(v => labelFor(optionalServicesOptions, v)).join(', ')
              : null
          },
          {
            label: "Shipping",
            value: s.shippingDestination
              ? `${s.shippingDestination}${s.postalCode ? ` · ${s.postalCode}` : ''}`
              : null
          },
          {
            label: "Attachments",
            value: typeof s.fileCount === 'number' && s.fileCount > 0
              ? `${s.fileCount} file${s.fileCount === 1 ? '' : 's'}`
              : null
          }
        ]}
      />
    );
  }


  return (
    <>
      {/* Breadcrumb Banner */}
      {onBackToServices && (
        <div className="bg-gradient-to-r from-slate-50 to-slate-100/80 px-6 md:px-8 py-3 border-b border-slate-200">
          <div className="flex items-center gap-3">
            <button
              onClick={onBackToServices}
              className="inline-flex items-center gap-2 text-[#0007b5] hover:text-[#0015e6] font-semibold text-sm transition-colors group"
            >
              <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
              All Services
            </button>
            <div className="w-0 h-0 border-t-[5px] border-t-transparent border-b-[5px] border-b-transparent border-l-[6px] border-l-slate-300" />
            <span className="text-slate-700 font-semibold text-sm">Custom Design</span>
          </div>
        </div>
      )}

      {/* Service Header */}
      <div className="px-6 md:px-8 py-4 border-b border-slate-100">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#0007b5] to-blue-600 flex items-center justify-center shadow-lg">
              <DraftingCompass className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="text-xl md:text-2xl font-bold text-slate-900">Custom Design Service</h2>
              <p className="text-sm text-slate-500">Need a custom 3D model? Our design team can bring your ideas to life.</p>
            </div>
          </div>
          <div className="text-sm text-slate-500 font-medium hidden sm:block">
            Step {currentStep + 1} of {totalSteps}
          </div>
        </div>
        {/* Progress bar */}
        <div className="mt-4 h-1.5 bg-slate-200 rounded-full overflow-hidden">
          <div 
            className="h-full bg-gradient-to-r from-[#0007b5] to-blue-500 transition-all duration-500 ease-out"
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>

    <div className="flex flex-col lg:flex-row">
      {/* Legend Sidebar - Width matches Instant Quote (lg:w-72) */}
      <div className={`
        ${legendOpen ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0 lg:max-h-none lg:opacity-100'}
        lg:w-72 lg:min-h-full overflow-hidden transition-all duration-300 ease-out
        bg-gradient-to-b from-[#0007b5] via-[#0010d9] to-blue-600
      `} data-testid="design-form-legend-sidebar">
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <span className="text-white/80 text-xs uppercase tracking-wider font-semibold">Steps</span>
            <button
              type="button"
              onClick={() => setLegendOpen(false)}
              className="lg:hidden p-1 rounded hover:bg-white/10 transition-colors"
            >
              <X className="w-4 h-4 text-white/80" />
            </button>
          </div>
          <nav className="space-y-2">
            {steps.map((step, index) => {
              const StepIcon = step.icon;
              const isActive = currentStep === index;
              const isPastStep = currentStep > index;
              const stepCompleted = isDesignStepCompleted(index);
              return (
                <button
                  key={index}
                  type="button"
                  onClick={() => goToStep(index)}
                  data-testid={`design-form-step-${index}`}
                  className={`w-full flex items-center gap-3 px-3 py-3 rounded-xl transition-all duration-200 text-left ${
                    isActive 
                      ? 'bg-white/20 text-white shadow-lg' 
                      : 'text-white/70 hover:bg-white/10 hover:text-white'
                  }`}
                >
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
                    isActive ? 'bg-white text-[#0007b5]' : 
                    isPastStep && stepCompleted ? 'bg-emerald-500 text-white' : 
                    isPastStep && !stepCompleted ? 'bg-amber-500 text-white' : 
                    'bg-white/10 text-white/60'
                  }`}>
                    {isPastStep ? (
                      stepCompleted ? <Check className="w-4 h-4" /> : <AlertTriangle className="w-4 h-4" />
                    ) : (
                      <StepIcon className="w-4 h-4" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className={`text-sm font-medium truncate ${isActive ? 'text-white' : ''}`}>
                      {step.title}
                    </p>
                    <p className="text-xs text-white/50">Step {index + 1}</p>
                  </div>
                </button>
              );
            })}
          </nav>
        </div>
      </div>

      {/* Form Content */}
      <div className="flex-1 p-6 md:p-8">
        <form onSubmit={handleSubmit}>
          {/* File Upload Section */}
          <MultiFileUpload
            files={formData.files}
            onFilesChange={(files) => setFormData({...formData, files})}
            fileInputId="design-service-file"
            accept=".pdf,.dwg,.dxf,.stl,.obj,.3mf,.ply,.amf,.step,.stp,.jpg,.jpeg,.png"
            emptyIcon={Upload}
            emptyTitle="Upload Reference File"
            emptySubtitle="Upload a reference to help us understand your project"
            emptyButtonText="Browse Files (Optional)"
            emptyNote="Images, PDFs, CAD files, DXF, DWG, sketches"
            renderPreview={(file) => <FilePreview file={file} />}
          />
          <div className="flex items-start gap-2 -mt-6 mb-8 text-xs text-slate-500">
            <Lock className="w-4 h-4 text-slate-700 flex-shrink-0 mt-0.5" />
            <p><span className="font-bold text-slate-700">You own your ideas and designs.</span> All uploads are confidential and not shared with any third parties.</p>
          </div>

          {/* Animated Step Content */}
          <div className="relative overflow-hidden min-h-[280px] px-1 pb-1">
            {/* Step 0: Project Details */}
            <div 
              className={`transition-all duration-300 ease-out ${
                currentStep === 0 
                  ? 'opacity-100 translate-x-0' 
                  : direction > 0 
                    ? 'opacity-0 -translate-x-full absolute inset-0' 
                    : 'opacity-0 translate-x-full absolute inset-0'
              }`}
            >
              <h3 className="text-lg font-semibold text-slate-900 mb-6">Project Details</h3>
              <div>
                <Label className="text-base font-medium">Describe your project</Label>
                <textarea
                  placeholder="Tell us about your project or idea, the intended end use, and any specific requirements or specifications..."
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  required
                  rows={7}
                  className="w-full mt-2 px-4 py-4 bg-white border-2 border-slate-200 rounded-xl focus:border-[#0007b5] focus:outline-none resize-none"
                />
              </div>
            </div>

            {/* Step 1: Application & Configuration */}
            <div 
              className={`transition-all duration-300 ease-out ${
                currentStep === 1 
                  ? 'opacity-100 translate-x-0' 
                  : currentStep > 1
                    ? 'opacity-0 -translate-x-full absolute inset-0' 
                    : 'opacity-0 translate-x-full absolute inset-0'
              }`}
            >
              <h3 className="text-lg font-semibold text-slate-900 mb-6">Application & Configuration</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <Label className="text-base font-medium">Application</Label>
                  <StyledSelector
                    value={formData.application}
                    onChange={(v) => setFormData({...formData, application: v})}
                    placeholder="Select application type"
                    options={applicationOptions.map(o => ({ id: o.value, name: o.label }))}
                  />
                  {formData.application === 'other' && (
                    <input
                      type="text"
                      placeholder="Please specify..."
                      value={formData.applicationOther}
                      onChange={(e) => setFormData({...formData, applicationOther: e.target.value})}
                      className="w-full mt-2 px-4 py-4 bg-white border-2 border-slate-200 rounded-xl focus:border-[#0007b5] focus:outline-none"
                    />
                  )}
                </div>
                <div>
                  <Label className="text-base font-medium">Configuration</Label>
                  <StyledSelector
                    value={formData.configuration}
                    onChange={(v) => setFormData({...formData, configuration: v})}
                    placeholder="Select configuration"
                    options={configurationOptions.map(o => ({ id: o.value, name: o.label }))}
                  />
                </div>
              </div>
            </div>

            {/* Step 2: Requirements */}
            <div 
              className={`transition-all duration-300 ease-out ${
                currentStep === 2 
                  ? 'opacity-100 translate-x-0' 
                  : currentStep > 2
                    ? 'opacity-0 -translate-x-full absolute inset-0' 
                    : 'opacity-0 translate-x-full absolute inset-0'
              }`}
            >
              <h3 className="text-lg font-semibold text-slate-900 mb-6">Requirements</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <Label className="text-base font-medium">3D Printing Requirements</Label>
                  <StyledSelector
                    value={formData.printingRequirements}
                    onChange={(v) => setFormData({...formData, printingRequirements: v})}
                    placeholder="Select printing requirements"
                    options={printingRequirementsOptions.map(o => ({ id: o.value, name: o.label }))}
                  />
                </div>
                <div>
                  <Label className="text-base font-medium">Fitment Requirements</Label>
                  <StyledSelector
                    value={formData.fitmentRequirements}
                    onChange={(v) => setFormData({...formData, fitmentRequirements: v})}
                    placeholder="Select fitment requirements"
                    options={fitmentOptions.map(o => ({ id: o.value, name: o.label }))}
                  />
                </div>
              </div>
            </div>

            {/* Step 3: Timeline & Services */}
            <div 
              className={`transition-all duration-300 ease-out ${
                currentStep === 3 
                  ? 'opacity-100 translate-x-0' 
                  : currentStep > 3
                    ? 'opacity-0 -translate-x-full absolute inset-0' 
                    : 'opacity-0 translate-x-full absolute inset-0'
              }`}
            >
              <h3 className="text-lg font-semibold text-slate-900 mb-6">Timeline & Services</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <Label className="text-base font-medium">How soon do you need a concept?</Label>
                  <StyledSelector
                    value={formData.timeline}
                    onChange={(v) => setFormData({...formData, timeline: v})}
                    placeholder="Select timeline"
                    options={timelineOptions.map(o => ({ id: o.value, name: o.label }))}
                  />
                  {formData.timeline === 'other' && (
                    <input
                      type="text"
                      placeholder="Please specify timeline..."
                      value={formData.timelineOther}
                      onChange={(e) => setFormData({...formData, timelineOther: e.target.value})}
                      className="w-full mt-2 px-4 py-4 bg-white border-2 border-slate-200 rounded-xl focus:border-[#0007b5] focus:outline-none"
                    />
                  )}
                </div>
                <div>
                  <Label className="text-base font-medium">Optional Services</Label>
                  <div className="mt-2 space-y-3">
                    {optionalServicesOptions.map(option => (
                      <label key={option.value} className="flex items-center gap-3 cursor-pointer group">
                        <div className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-all ${
                          formData.optionalServices.includes(option.value)
                            ? 'bg-[#0007b5] border-[#0007b5]'
                            : 'border-slate-300 group-hover:border-[#0007b5]'
                        }`}>
                          {formData.optionalServices.includes(option.value) && (
                            <Check className="w-3 h-3 text-white" />
                          )}
                        </div>
                        <input
                          type="checkbox"
                          checked={formData.optionalServices.includes(option.value)}
                          onChange={() => handleOptionalServiceChange(option.value)}
                          className="hidden"
                        />
                        <span className="text-slate-700">{option.label}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Step 4: Contact Info */}
            <div 
              className={`transition-all duration-300 ease-out ${
                currentStep === 4 
                  ? 'opacity-100 translate-x-0' 
                  : 'opacity-0 translate-x-full absolute inset-0'
              }`}
            >
              <h3 className="text-lg font-semibold text-slate-900 mb-6">Contact Information</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <Label className="text-base font-medium">Name</Label>
                  <input
                    type="text"
                    placeholder="Your name"
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    required
                    className="w-full mt-2 px-4 py-4 bg-white border-2 border-slate-200 rounded-xl focus:border-[#0007b5] focus:outline-none"
                  />
                </div>
                <div>
                  <Label className="text-base font-medium">Email</Label>
                  <input
                    type="email"
                    placeholder="you@example.com"
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    required
                    className="w-full mt-2 px-4 py-4 bg-white border-2 border-slate-200 rounded-xl focus:border-[#0007b5] focus:outline-none"
                  />
                </div>
                <div>
                  <Label className="text-base font-medium">Company Name <span className="text-sm font-normal text-slate-400">(Optional)</span></Label>
                  <input
                    type="text"
                    placeholder="Your company"
                    value={formData.companyName}
                    onChange={(e) => setFormData({...formData, companyName: e.target.value})}
                    className="w-full mt-2 px-4 py-4 bg-white border-2 border-slate-200 rounded-xl focus:border-[#0007b5] focus:outline-none"
                  />
                </div>
                <div>
                  <Label className="text-base font-medium">Phone Number <span className="text-sm font-normal text-slate-400">(Optional)</span></Label>
                  <input
                    type="tel"
                    placeholder="(555) 123-4567"
                    value={formData.phone}
                    onChange={(e) => setFormData({...formData, phone: e.target.value})}
                    className="w-full mt-2 px-4 py-4 bg-white border-2 border-slate-200 rounded-xl focus:border-[#0007b5] focus:outline-none"
                  />
                </div>
              </div>

              {/* Shipping Details */}
              <div className="mt-8 pt-6 border-t border-slate-200">
                <h3 className="text-lg font-semibold text-slate-900 mb-1">Shipping Details</h3>
                <p className="text-sm text-slate-500 mb-6">If required for shipping prototypes or final components.</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <Label className="text-base font-medium">Shipping Destination</Label>
                    <StyledSelector
                      value={formData.shippingDestination}
                      onChange={(v) => setFormData({...formData, shippingDestination: v})}
                      placeholder="Choose destination"
                      options={[
                        { id: 'CA', name: 'Canada' },
                        { id: 'US', name: 'United States' },
                      ]}
                    />
                  </div>
                  <div>
                    <Label className="text-base font-medium">Postal/Zip Code (Optional)</Label>
                    <input
                      type="text"
                      placeholder={formData.shippingDestination === "CA" ? "A1A 1A1" : "12345"}
                      value={formData.postalCode}
                      onChange={(e) => setFormData({...formData, postalCode: e.target.value})}
                      className="w-full mt-2 px-4 py-4 bg-white border-2 border-slate-200 rounded-xl focus:border-[#0007b5] focus:outline-none"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Navigation */}
          <div className="flex items-center justify-between mt-8 pt-6 border-t border-slate-200">
            <button
              type="button"
              onClick={prevStep}
              className={`flex items-center gap-2 px-4 py-2 text-slate-600 hover:text-slate-900 transition-colors ${currentStep === 0 ? 'invisible' : ''}`}
            >
              <ArrowLeft className="w-4 h-4" />
              Back
            </button>

            {currentStep < totalSteps - 1 ? (
              <button
                type="button"
                onClick={nextStep}
                className="flex items-center gap-2 px-6 py-3 bg-[#0007b5] text-white rounded-xl hover:bg-[#000690] transition-colors font-medium"
              >
                Next
                <ArrowRight className="w-4 h-4" />
              </button>
            ) : (
              <button
                type="submit"
                disabled={submitting || !isDesignStepCompleted(0) || !isDesignStepCompleted(1) || !isDesignStepCompleted(2) || !isDesignStepCompleted(3) || !isDesignStepCompleted(4)}
                className={`flex items-center gap-2 px-6 py-3 rounded-xl font-medium transition-all ${
                  !submitting && isDesignStepCompleted(0) && isDesignStepCompleted(1) && isDesignStepCompleted(2) && isDesignStepCompleted(3) && isDesignStepCompleted(4)
                    ? 'bg-[#0007b5] text-white hover:bg-[#000690] shadow-lg shadow-blue-500/20 cursor-pointer'
                    : 'bg-[#0007b5]/40 text-white/70 cursor-not-allowed'
                }`}
              >
                {submitting ? 'Submitting…' : 'Submit Request'}
                <ArrowRight className="w-4 h-4" />
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
    </>
  );
};

const ServicesToolPage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const { addToCart } = useCart();
  const { getIdToken, isAuthenticated, loading: authLoading } = useAuth();
  const initialTab = searchParams.get('service') || searchParams.get('tab') || null;
  const startInTool = !!initialTab;
  
  const [activeTab, setActiveTab] = useState(initialTab || 'on-demand-3d-print');
  const [showServiceSelection, setShowServiceSelection] = useState(!startInTool);
  const [showSwitchWarning, setShowSwitchWarning] = useState(false);
  const [pendingService, setPendingService] = useState(null);
  const [hasFormData, setHasFormData] = useState(false);
  const [loadingQuote, setLoadingQuote] = useState(false);
  const [quoteData, setQuoteData] = useState(null);
  const [detectedUnits, setDetectedUnits] = useState({ units: null, message: null });
  const [multiFileSettings, setMultiFileSettings] = useState(''); // '', 'same', 'different'
  const [selectedFileIndex, setSelectedFileIndex] = useState(0);
  
  // Quote Wizard State
  const [quoteStep, setQuoteStep] = useState(0);
  const quoteSteps = [
    { title: "Material & Quality", icon: Boxes, fields: ["material", "quality"] },
    { title: "Options", icon: SlidersHorizontal, fields: ["strength", "smoothing"] },
    { title: "Color & Quantity", icon: Palette, fields: ["color", "quantity"] },
    { title: "Contact Info", icon: User, fields: ["contact_name", "contact_email"] },
    { title: "Shipping", icon: MapPin, fields: ["country_code", "postal_code"] },
    { title: "Review & Submit", icon: FileText, fields: ["notes"] }
  ];
  const totalQuoteSteps = quoteSteps.length;
  const quoteProgress = ((quoteStep + 1) / totalQuoteSteps) * 100;

  const [formData, setFormData] = useState({
    files: [],
    material: "",
    strength: "",
    quality: "",
    color: "",
    customColor: "",
    quantity: 1,
    contact_name: "",
    contact_email: "",
    country_code: "",
    postal_code: "",
    notes: "",
    units: "mm",
    smoothing: "",
    insertsRequired: "no",
    insertsUnits: "metric",
    inserts: [] // { id, position: {x,y,z}, type: 'threaded'|'bushing', size, customSize, note }
  });

  const [markersMode, setMarkersMode] = useState(false);
  const [modelSnapshot, setModelSnapshot] = useState(null);
  const [leadTimeAdjustment, setLeadTimeAdjustment] = useState({ value: 0, unit: 'days' });
  const [reuseToast, setReuseToast] = useState(null);
  const [materialInventory, setMaterialInventory] = useState([]);
  const [materialCatalog, setMaterialCatalog] = useState([]);
  // Notify-me flow for sold-out materials (inline mini-form in Material step)
  const [notifyOpen, setNotifyOpen] = useState(false);
  const [notifyEmail, setNotifyEmail] = useState('');
  const [notifyStatus, setNotifyStatus] = useState({ type: 'idle', message: '' });

  // Fetch material availability/"more coming" inventory once. Consumed by
  // MaterialSwatchSelector to hide sold-out materials & tag restock ETAs.
  React.useEffect(() => {
    (async () => {
      try {
        const [invRes, catRes] = await Promise.all([
          fetch(`${API}/material-inventory`),
          fetch(`${API}/materials-catalog`),
        ]);
        if (invRes.ok) {
          const data = await invRes.json();
          setMaterialInventory(data.materials || []);
        }
        if (catRes.ok) {
          const data = await catRes.json();
          setMaterialCatalog(data.materials || []);
        }
      } catch (err) {
        // Silent fail: default to full availability + hardcoded catalog.
      }
    })();
  }, []);

  // Reset the notify-me inline form whenever the selected material changes
  // so the UI doesn't carry over stale success/error messages.
  React.useEffect(() => {
    setNotifyOpen(false);
    setNotifyEmail('');
    setNotifyStatus({ type: 'idle', message: '' });
  }, [formData.material]);

  // Fetch lead-time adjustment once on mount so the quote summary can reflect current capacity
  React.useEffect(() => {
    const fetchLeadTimeAdjustment = async () => {
      try {
        const response = await fetch(`${API}/lead-time-adjustment`);
        if (response.ok) {
          const data = await response.json();
          setLeadTimeAdjustment(data);
        }
      } catch (error) {
        console.error('Error fetching lead time adjustment:', error);
      }
    };
    fetchLeadTimeAdjustment();
  }, []);

  // Pick up file from landing page navigation
  React.useEffect(() => {
    if (location.state?.uploadedFile) {
      setFormData(prev => ({ ...prev, files: [location.state.uploadedFile] }));
      // Clear the state so it doesn't re-apply on re-renders
      window.history.replaceState({}, document.title);
    }
  }, []);

  // "Order Part" from Profile → Parts dashboard: re-load a file from a prior quote
  React.useEffect(() => {
    const reuseId = searchParams.get('reuse');
    if (!reuseId) return;
    // Source can be either 'quote' or 'service'; default to quote for backwards
    // compatibility with older links.
    const reuseSource = searchParams.get('source') === 'service' ? 'service' : 'quote';
    if (authLoading) return;
    if (!isAuthenticated) {
      alert('Please sign in to reorder a part from your dashboard.');
      return;
    }
    setActiveTab('on-demand-3d-print');
    setShowServiceSelection(false);

    (async () => {
      let restoredFromConfig = false;
      try {
        const token = await getIdToken();
        if (!token) {
          alert('Please sign in to reorder a part from your dashboard.');
          return;
        }
        // 1) Download the file blob
        const res = await fetch(`${API}/me/files/${reuseSource}/${reuseId}/download`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) {
          alert("We couldn't find that file — it may have been deleted.");
          return;
        }
        const blob = await res.blob();
        // Prefer the filename supplied by the dashboard (it's the original
        // uploaded name including the correct extension — critical for the
        // 3D preview to pick the right loader). Fall back to the
        // Content-Disposition header, then to a safe default.
        let filename = searchParams.get('name') || '';
        if (!filename) {
          const disposition = res.headers.get('Content-Disposition') || '';
          const match = disposition.match(/filename="?([^";]+)"?/i);
          if (match && match[1]) filename = match[1];
        }
        if (!filename) filename = `part-${reuseId}.stl`;
        const file = new File([blob], filename, {
          type: blob.type || 'application/octet-stream',
        });

        // 2) For quote files, also pull the saved configuration so we can
        //    pre-fill material/color/quality etc. for one-click reorder.
        let savedConfig = null;
        if (reuseSource === 'quote') {
          try {
            const cfgRes = await fetch(`${API}/me/files/quote/${reuseId}/config`, {
              headers: { Authorization: `Bearer ${token}` },
            });
            if (cfgRes.ok) {
              const data = await cfgRes.json();
              savedConfig = data.config || null;
            }
          } catch (cfgErr) {
            // Non-fatal: just skip the config restore
            console.warn('Could not restore saved config:', cfgErr);
          }
        }

        setFormData(prev => {
          const next = { ...prev, files: [file] };
          if (savedConfig && Object.keys(savedConfig).length > 0) {
            if (savedConfig.material !== undefined) next.material = savedConfig.material;
            if (savedConfig.strength !== undefined) next.strength = savedConfig.strength;
            if (savedConfig.quality !== undefined) next.quality = savedConfig.quality;
            if (savedConfig.color !== undefined) next.color = savedConfig.color;
            if (savedConfig.quantity !== undefined) next.quantity = savedConfig.quantity;
            if (savedConfig.units !== undefined) next.units = savedConfig.units;
            if (savedConfig.smoothing !== undefined) next.smoothing = savedConfig.smoothing;
            if (savedConfig.notes !== undefined) next.notes = savedConfig.notes;
            if (savedConfig.contact_name !== undefined) next.contact_name = savedConfig.contact_name;
            if (savedConfig.contact_email !== undefined) next.contact_email = savedConfig.contact_email;
            if (savedConfig.country_code !== undefined) next.country_code = savedConfig.country_code;
            if (savedConfig.postal_code !== undefined) next.postal_code = savedConfig.postal_code;
            if (savedConfig.inserts_required !== undefined) next.insertsRequired = savedConfig.inserts_required;
            if (savedConfig.inserts_units !== undefined) next.insertsUnits = savedConfig.inserts_units;
            if (Array.isArray(savedConfig.inserts)) next.inserts = savedConfig.inserts;
            restoredFromConfig = true;
          }
          return next;
        });

        // 3) If config was restored, skip to the final review step so the
        //    user can place the order with a single additional click.
        if (restoredFromConfig) {
          // Review is the last step (index = totalQuoteSteps - 1)
          setQuoteStep(quoteSteps.length - 1);
          // Toast-style banner; setTimeout to let state settle first.
          setTimeout(() => {
            setReuseToast("Configuration restored from your previous order — review and place your order.");
          }, 50);
        }
      } catch (err) {
        console.error('Failed to preload file for reorder:', err);
        alert("Something went wrong preloading your file. Please try uploading it manually.");
      } finally {
        const next = new URLSearchParams(searchParams);
        next.delete('reuse');
        next.delete('source');
        next.delete('name');
        setSearchParams(next, { replace: true });
      }
    })();
  }, [authLoading, isAuthenticated]); // eslint-disable-line

  // Function to check if a step is completed
  const isStepCompleted = (stepIndex) => {
    const step = quoteSteps[stepIndex];
    if (!step) return false;
    
    // Step 0: Material & Quality - also requires file upload
    if (stepIndex === 0) {
      if (formData.files.length === 0) return false;
      if (!formData.material || !formData.quality) return false;
      return true;
    }
    
    // Step 1: Options
    if (stepIndex === 1) {
      if (!formData.strength || !formData.smoothing) return false;
      // If inserts are required, each placed marker must have type and size (custom requires customSize text)
      if (formData.insertsRequired === 'yes' && formData.inserts.length > 0) {
        const allValid = formData.inserts.every(m => m.type && m.size && (m.size !== 'Other' || (m.customSize && m.customSize.trim().length > 0)));
        if (!allValid) return false;
      }
      return true;
    }
    
    // Step 2: Color & Quantity
    if (stepIndex === 2) {
      if (!formData.color) return false;
      if (formData.quantity < 1) return false;
      return true;
    }
    
    // Step 3: Contact Info
    if (stepIndex === 3) {
      if (!formData.contact_name || !formData.contact_email) return false;
      return true;
    }
    
    // Step 4: Shipping - postal code is optional
    if (stepIndex === 4) {
      if (!formData.country_code) return false;
      return true;
    }
    
    // Step 5: Review & Submit - always considered complete if reached
    if (stepIndex === 5) {
      return true;
    }
    
    return false;
  };

  // Note: Service form state is now managed internally by each wizard component
  // Only keeping submittingService for compatibility with handleServiceRequest
  const [submittingService, setSubmittingService] = useState(false);

  // Dirty-state tracking for switch-service warning across all wizards.
  // Each child wizard calls onDirtyChange whenever the user has edited any field.
  const [childDirty, setChildDirty] = useState(false);

  // Handle launching a service from the selection screen
  const handleLaunchService = (serviceId) => {
    setActiveTab(serviceId);
    setShowServiceSelection(false);
    setSearchParams({ service: serviceId });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Deep-equality-lite dirty check for the Instant Quote wizard.
  // Returns true if the user has selected/typed anything at all.
  const isInstantQuoteDirty = () => {
    return !!(
      formData.files.length > 0 ||
      formData.material ||
      formData.strength ||
      formData.quality ||
      formData.color ||
      formData.customColor ||
      formData.quantity !== 1 ||
      formData.contact_name ||
      formData.contact_email ||
      formData.country_code ||
      formData.postal_code ||
      formData.notes ||
      (formData.smoothing && formData.smoothing !== "") ||
      formData.insertsRequired === "yes" ||
      (formData.inserts && formData.inserts.length > 0)
    );
  };

  // Handle going back to service selection
  const handleBackToServices = () => {
    // Show warning if the current service has any user input (quote wizard fields
    // OR any child wizard that registered as dirty).
    const quoteDirty = activeTab === 'on-demand-3d-print' && isInstantQuoteDirty();
    if (quoteDirty || childDirty) {
      setShowSwitchWarning(true);
      setPendingService(null);
    } else {
      setShowServiceSelection(true);
      setSearchParams({});
    }
  };

  // Handle confirming the switch
  const handleConfirmSwitch = () => {
    setShowSwitchWarning(false);
    if (pendingService) {
      setActiveTab(pendingService);
      setSearchParams({ service: pendingService });
      setPendingService(null);
    } else {
      setShowServiceSelection(true);
      setSearchParams({});
    }
    // Reset form data
    setFormData({
      files: [],
      material: "",
      strength: "",
      quality: "",
      color: "",
      customColor: "",
      quantity: 1,
      contact_name: "",
      contact_email: "",
      country_code: "",
      postal_code: "",
      notes: "",
      units: "mm",
      smoothing: ""
    });
    setQuoteStep(0);
    setQuoteData(null);
    setMultiFileSettings('');
    setSelectedFileIndex(0);
    setModelSnapshot(null);
    // Clear any registered child-wizard dirty flag — the wizard is about to unmount
    setChildDirty(false);
  };

  // Format numbers with thousand separators and 2 decimals (used across the quote summary)
  const fmt = (n) => {
    if (n === null || n === undefined || isNaN(Number(n))) return '0.00';
    return Number(n).toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  const nextQuoteStep = () => {
    if (quoteStep < totalQuoteSteps - 1) {
      setQuoteStep(prev => prev + 1);
    }
  };

  const prevQuoteStep = () => {
    if (quoteStep > 0) {
      setQuoteStep(prev => prev - 1);
    }
  };

  const goToQuoteStep = (step) => {
    setQuoteStep(step);
  };

  const handleQuoteSubmit = async (e) => {
    e.preventDefault();
    setLoadingQuote(true);

    // Capture a tight, grid-less snapshot of the 3D viewer (preserves user's orientation)
    try {
      const canvasEl = document.querySelector('canvas[data-threed-main-canvas]');
      let snap = null;
      if (canvasEl && typeof canvasEl.__captureTightSnapshot === 'function') {
        snap = canvasEl.__captureTightSnapshot();
      } else if (canvasEl && typeof canvasEl.toDataURL === 'function') {
        snap = canvasEl.toDataURL('image/png');
      }
      if (snap && snap.length > 1000) {
        setModelSnapshot(snap);
      }
    } catch (snapErr) {
      console.warn('Could not capture model snapshot:', snapErr);
    }
    
    try {
      const filesToQuote = formData.files.length > 1 && multiFileSettings === 'same'
        ? formData.files
        : [formData.files[selectedFileIndex] || formData.files[0]];

      const results = [];
      const errors = [];
      for (const file of filesToQuote) {
        const formDataToSend = new FormData();
        formDataToSend.append('file', file);
        formDataToSend.append('material', formData.material);
        formDataToSend.append('strength', formData.strength);
        formDataToSend.append('quality', formData.quality);
        formDataToSend.append('color', formData.color === 'Other' ? formData.customColor : formData.color);
        formDataToSend.append('quantity', formData.quantity.toString());
        formDataToSend.append('contact_name', formData.contact_name);
        formDataToSend.append('contact_email', formData.contact_email);
        formDataToSend.append('country_code', formData.country_code);
        formDataToSend.append('postal_code', formData.postal_code);
        formDataToSend.append('notes', formData.notes);
        formDataToSend.append('units', formData.units);
        formDataToSend.append('smoothing', formData.smoothing);
        formDataToSend.append('inserts_required', formData.insertsRequired);
        formDataToSend.append('inserts_units', formData.insertsUnits);
        formDataToSend.append('inserts', JSON.stringify(formData.inserts));

        const response = await fetch(`${API}/quote/3d-print`, {
          method: 'POST',
          body: formDataToSend
        });

        if (response.ok) {
          const data = await response.json();
          results.push({ ...data, fileName: file.name });
        } else {
          let detail = `${response.status} ${response.statusText}`;
          try {
            const err = await response.json();
            if (Array.isArray(err?.detail)) {
              detail = err.detail.map(d => `${(d.loc || []).slice(-1)[0] || 'field'}: ${d.msg}`).join('; ');
            } else if (typeof err?.detail === 'string') {
              detail = err.detail;
            }
          } catch { /* response body not JSON */ }
          console.error(`Quote failed for "${file.name}":`, detail);
          errors.push(`${file.name}: ${detail}`);
        }
      }

      if (errors.length > 0 && results.length === 0) {
        alert(
          "We couldn't generate your quote right now. Please review your selections and try again. " +
          "If the problem persists, contact support@create3dworks.com and we'll sort it out."
        );
      }

      if (results.length > 0) {
        if (results.length === 1) {
          // Normalize so the UI has a reliable total_price field regardless of backend shape
          const r = results[0];
          const total = r.final_price ?? r.price_breakdown?.total_price ?? r.total_price ?? 0;
          setQuoteData({ ...r, total_price: total });
        } else {
          // Combined multi-file quote
          const totalPrice = results.reduce(
            (sum, r) => sum + (r.final_price ?? r.price_breakdown?.total_price ?? r.total_price ?? 0),
            0
          );
          setQuoteData({
            isMultiFile: true,
            items: results.map(r => ({
              ...r,
              total_price: r.final_price ?? r.price_breakdown?.total_price ?? r.total_price ?? 0,
            })),
            total_price: totalPrice
          });
        }
        setActiveTab('quote');
        trackQuoteSubmission(formData.material, formData.quantity);
      }
    } catch (error) {
      console.error('Quote submission error:', error);
    } finally {
      setLoadingQuote(false);
    }
  };

  // Handle adding a single part to cart in "different settings" flow and removing it from files
  const handleAddPartToCartAndNext = () => {
    // The current file/quote has been added to cart via existing cart logic
    // Remove the current file from the list
    const updatedFiles = formData.files.filter((_, i) => i !== selectedFileIndex);
    setFormData(prev => ({
      ...prev,
      files: updatedFiles,
      // Reset settings for next part
      material: "",
      strength: "",
      quality: "",
      color: "",
      customColor: "",
      quantity: 1,
      notes: "",
      smoothing: "",
      insertsRequired: "no",
      insertsUnits: "metric",
      inserts: []
    }));
    setMarkersMode(false);
    setSelectedFileIndex(0);
    setQuoteStep(0);
    setQuoteData(null);
    setModelSnapshot(null);
    setActiveTab('on-demand-3d-print');
  };

  // Build a cart item from a single quote result (shape from /api/quote/3d-print)
  const buildCartItemFromQuote = (q, fileName) => {
    const total =
      q.total_price ??
      q.final_price ??
      q.price_breakdown?.total_price ??
      0;
    const name = fileName || q.fileName || q.filename || 'Custom 3D Print';
    const color = formData.color === 'Other' ? formData.customColor : formData.color;
    const description = [
      `File: ${name}`,
      `Material: ${formData.material}${color ? ` - ${color}` : ''}`,
      `Quality: ${formData.quality}`,
      `Strength: ${formData.strength}`,
      `Smoothing: ${formData.smoothing}`,
      formData.insertsRequired === 'yes' && formData.inserts.length > 0
        ? `Metal inserts: ${formData.inserts.length}`
        : null
    ].filter(Boolean).join('\n');

    return {
      id: `quote-${q.quote_id}`,
      quote_id: q.quote_id,
      type: 'print_quote',
      name: `Print Order — ${name}`,
      description,
      price: total,
      image: 'https://img.icons8.com/color/150/000000/3d-printer.png',
      image_url: 'https://img.icons8.com/color/150/000000/3d-printer.png',
      category: 'Custom 3D Print',
      manufacturer: 'On-Demand Print',
      in_stock: true,
      quantity: 1,
      quote_data: {
        quote_id: q.quote_id,
        filename: name,
        material: formData.material,
        color,
        quality: formData.quality,
        strength: formData.strength,
        smoothing: formData.smoothing,
        surface_area: q.surface_area,
        price_breakdown: q.price_breakdown,
        estimated_time: q.estimated_time,
        delivery_weeks: q.delivery_weeks,
        production_time: q.production_time,
        contact_email: formData.contact_email,
        units: formData.units,
        shipping_country_code: formData.country_code,
        inserts_required: formData.insertsRequired,
        inserts_units: formData.insertsUnits,
        inserts: formData.inserts
      }
    };
  };

  // Add the current quote (single or multi-file) to cart and go to cart page
  const handleAddQuoteToCart = () => {
    if (!quoteData) return;
    if (quoteData.isMultiFile && Array.isArray(quoteData.items)) {
      quoteData.items.forEach((item) => {
        addToCart(buildCartItemFromQuote(item, item.fileName));
      });
    } else {
      addToCart(buildCartItemFromQuote(quoteData, quoteData.fileName));
    }
    window.scrollTo({ top: 0, behavior: 'smooth' });
    navigate('/cart');
  };

  // Take user back to the wizard Review step with all selections preserved
  const handleEditQuote = () => {
    setActiveTab('on-demand-3d-print');
    setQuoteStep(5);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };


  const handleServiceRequest = async (serviceType, serviceFormData, file = null) => {
    setSubmittingService(true);
    try {
      let endpoint;
      let body;
      let headers = {};

      if (serviceType === "2D to 3D Conversion") {
        if (!file) {
          alert('File upload is required for this service');
          setSubmittingService(false);
          return { ok: false };
        }
        endpoint = `${API}/request/service-with-file`;
        body = new FormData();
        body.append('service_type', serviceType);
        body.append('contact_name', serviceFormData.name);
        body.append('contact_email', serviceFormData.email);
        body.append('description', serviceFormData.description);
        body.append('file', file);
        if (serviceFormData.companyName) body.append('company_name', serviceFormData.companyName);
        if (serviceFormData.phone) body.append('phone', serviceFormData.phone);
        if (serviceFormData.shippingDestination) body.append('shipping_destination', serviceFormData.shippingDestination);
        if (serviceFormData.postalCode) body.append('postal_code', serviceFormData.postalCode);
        if (serviceFormData.application) body.append('application', serviceFormData.application);
        if (serviceFormData.configuration) body.append('configuration', serviceFormData.configuration);
        if (serviceFormData.printingRequirements) body.append('printing_requirements', serviceFormData.printingRequirements);
        if (serviceFormData.fitmentRequirements) body.append('fitment_requirements', serviceFormData.fitmentRequirements);
        if (serviceFormData.timeline) body.append('timeline', serviceFormData.timeline);
        if (Array.isArray(serviceFormData.optionalServices) && serviceFormData.optionalServices.length > 0) {
          body.append('optional_services', serviceFormData.optionalServices.join(','));
        }
      } else if (serviceType === "Product Optimization" || serviceType === "Product Renders" || serviceType === "Renders") {
        if (!file) {
          alert('File upload is required for this service');
          setSubmittingService(false);
          return { ok: false };
        }
        endpoint = `${API}/request/service-with-file`;
        body = new FormData();
        body.append('service_type', serviceType);
        body.append('contact_name', serviceFormData.name);
        body.append('contact_email', serviceFormData.email);
        body.append('description', serviceFormData.description);
        body.append('file', file);
        if (serviceFormData.companyName) body.append('company_name', serviceFormData.companyName);
        if (serviceFormData.phone) body.append('phone', serviceFormData.phone);
        if (serviceFormData.shippingDestination) body.append('shipping_destination', serviceFormData.shippingDestination);
        if (serviceFormData.postalCode) body.append('postal_code', serviceFormData.postalCode);
        if (serviceFormData.application) body.append('application', serviceFormData.application);
        if (serviceFormData.configuration) body.append('configuration', serviceFormData.configuration);
        if (serviceFormData.printingRequirements) body.append('printing_requirements', serviceFormData.printingRequirements);
        if (serviceFormData.fitmentRequirements) body.append('fitment_requirements', serviceFormData.fitmentRequirements);
        if (serviceFormData.timeline) body.append('timeline', serviceFormData.timeline);
        if (Array.isArray(serviceFormData.optionalServices) && serviceFormData.optionalServices.length > 0) {
          body.append('optional_services', serviceFormData.optionalServices.join(','));
        }
      } else if (serviceType === "Design Service") {
        endpoint = `${API}/request/service-optional-file`;
        body = new FormData();
        body.append('service_type', serviceType);
        body.append('contact_name', serviceFormData.name);
        body.append('contact_email', serviceFormData.email);
        body.append('description', serviceFormData.description);
        if (serviceFormData.companyName) {
          body.append('company_name', serviceFormData.companyName);
        }
        if (serviceFormData.phone) {
          body.append('phone', serviceFormData.phone);
        }
        if (serviceFormData.shippingDestination) {
          body.append('shipping_destination', serviceFormData.shippingDestination);
        }
        if (serviceFormData.postalCode) {
          body.append('postal_code', serviceFormData.postalCode);
        }
        // Design-wizard specifics so admin can see full request details
        if (serviceFormData.application) {
          body.append('application', serviceFormData.application);
        }
        if (serviceFormData.configuration) {
          body.append('configuration', serviceFormData.configuration);
        }
        if (serviceFormData.printingRequirements) {
          body.append('printing_requirements', serviceFormData.printingRequirements);
        }
        if (serviceFormData.fitmentRequirements) {
          body.append('fitment_requirements', serviceFormData.fitmentRequirements);
        }
        if (serviceFormData.timeline) {
          body.append('timeline', serviceFormData.timeline);
        }
        if (Array.isArray(serviceFormData.optionalServices) && serviceFormData.optionalServices.length > 0) {
          body.append('optional_services', serviceFormData.optionalServices.join(','));
        }
        if (file) {
          body.append('file', file);
        }
      } else {
        endpoint = `${API}/request/service`;
        headers['Content-Type'] = 'application/json';
        body = JSON.stringify({
          service_type: serviceType,
          contact_name: serviceFormData.name,
          contact_email: serviceFormData.email,
          description: serviceFormData.description,
          files_attached: false
        });
      }

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: headers,
        body: body,
      });

      if (response.ok) {
        trackServiceRequest(serviceType);
        let responseData = null;
        try { responseData = await response.json(); } catch { /* ignore */ }
        // These wizards own their own success UI — don't alert on top.
        const ownsSuccessUI = (
          serviceType === "Design Service" ||
          serviceType === "2D to 3D Conversion" ||
          serviceType === "Product Optimization" ||
          serviceType === "Product Renders" ||
          serviceType === "Renders"
        );
        if (!ownsSuccessUI) {
          alert('Service request submitted successfully! We will contact you within 24 hours.');
        }
        return { ok: true, data: responseData };
      } else {
        alert('Error submitting service request. Please try again.');
        return { ok: false };
      }
    } catch (error) {
      console.error('Service request error:', error);
      alert('Error submitting service request. Please try again.');
      return { ok: false };
    } finally {
      setSubmittingService(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <MainNavigation />
      
      {/* Switch Warning Modal */}
      <SwitchWarningModal 
        isOpen={showSwitchWarning}
        onConfirm={handleConfirmSwitch}
        onCancel={() => setShowSwitchWarning(false)}
      />
      
      {/* Full Width Services Tool */}
      <div className="pt-20">
        {/* Hero Header */}
        <div className="bg-gradient-to-br from-[#00068a] via-[#000cb8] to-[#0015e6] text-white py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center">
              <h1 className="text-4xl md:text-5xl font-bold tracking-tight mb-4">
                Start Your Project
              </h1>
              <p className="text-xl text-blue-100 max-w-2xl mx-auto">
                Get an instant print-on-demand quote, or request our services at any stage of your project from concept to finished product
              </p>
            </div>
          </div>
        </div>

        {/* Conditional Content: Service Selection OR Tool */}
        {showServiceSelection ? (
          /* Service Selection Accordion View */
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-8 pb-16">
            <ServiceSelectionAccordion 
              selectedService={activeTab}
              onServiceSelect={(id) => setActiveTab(id)}
              onLaunch={handleLaunchService}
            />
          </div>
        ) : (
          /* Tool View */
          <>
            {/* Main Tool Container - Overlapping Header */}
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-8 pb-8">
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            {/* Unified Form Container */}
            <div className="bg-white rounded-2xl shadow-2xl border border-slate-200/60 overflow-hidden">

              {/* Upload & Quote Tab */}
              <TabsContent value="on-demand-3d-print" className="mt-0">
                {/* Configuration restored banner */}
                {reuseToast && (
                  <div
                    className="mx-6 md:mx-8 mt-4 flex items-start gap-3 px-4 py-3 rounded-xl bg-emerald-50 border border-emerald-200 text-emerald-900"
                    data-testid="reuse-config-banner"
                  >
                    <div className="w-8 h-8 rounded-lg bg-white border border-emerald-200 flex items-center justify-center flex-shrink-0">
                      <Check className="w-4 h-4 text-emerald-600" />
                    </div>
                    <div className="flex-1 text-sm">
                      <div className="font-semibold">One-click reorder ready</div>
                      <div className="text-emerald-800">{reuseToast}</div>
                    </div>
                    <button
                      onClick={() => setReuseToast(null)}
                      className="text-emerald-700 hover:text-emerald-900 font-bold text-lg leading-none px-1"
                      aria-label="Dismiss"
                      data-testid="reuse-config-banner-close"
                    >
                      ×
                    </button>
                  </div>
                )}
                {/* Breadcrumb Banner */}
                <div className="bg-gradient-to-r from-slate-50 to-slate-100/80 px-6 md:px-8 py-3 border-b border-slate-200">
                  <div className="flex items-center gap-3">
                    <button
                      onClick={handleBackToServices}
                      className="inline-flex items-center gap-2 text-[#0007b5] hover:text-[#0015e6] font-semibold text-sm transition-colors group"
                    >
                      <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
                      All Services
                    </button>
                    <div className="w-0 h-0 border-t-[5px] border-t-transparent border-b-[5px] border-b-transparent border-l-[6px] border-l-slate-300" />
                    <span className="text-slate-700 font-semibold text-sm">Instant Quote</span>
                  </div>
                </div>

                {/* Service Header */}
                <div className="px-6 md:px-8 py-4 border-b border-slate-100">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#0007b5] to-blue-600 flex items-center justify-center shadow-lg">
                        <Upload className="w-6 h-6 text-white" />
                      </div>
                      <div>
                        <h2 className="text-xl md:text-2xl font-bold text-slate-900">Instant Quote</h2>
                        <p className="text-sm text-slate-500">Upload your 3D file and configure your print</p>
                      </div>
                    </div>
                    <div className="text-sm text-slate-500 font-medium hidden sm:block">
                      Step {quoteStep + 1} of {totalQuoteSteps}
                    </div>
                  </div>
                  {/* Progress bar */}
                  <div className="mt-4 h-1.5 bg-slate-200 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-gradient-to-r from-[#0007b5] to-blue-500 transition-all duration-500 ease-out"
                      style={{ width: `${quoteProgress}%` }}
                    />
                  </div>
                </div>

                <div className="flex flex-col lg:flex-row">
                  {/* Sidebar */}
                  <div className="hidden lg:block lg:w-72 bg-gradient-to-b from-[#0007b5] via-[#0010d9] to-blue-600">
                    <div className="p-6">
                      <span className="text-white/80 text-xs uppercase tracking-wider font-semibold">Steps</span>
                      <nav className="space-y-2 mt-4">
                        {quoteSteps.map((step, index) => {
                          const StepIcon = step.icon;
                          const isActive = quoteStep === index;
                          const isPastStep = quoteStep > index;
                          const stepCompleted = isStepCompleted(index);
                          return (
                            <button
                              key={index}
                              type="button"
                              onClick={() => goToQuoteStep(index)}
                              className={`w-full flex items-center gap-3 px-3 py-3 rounded-xl transition-all duration-200 text-left ${
                                isActive 
                                  ? 'bg-white/20 text-white shadow-lg' 
                                  : 'text-white/70 hover:bg-white/10 hover:text-white'
                              }`}
                            >
                              <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
                                isActive ? 'bg-white text-[#0007b5]' : 
                                isPastStep && stepCompleted ? 'bg-emerald-500 text-white' : 
                                isPastStep && !stepCompleted ? 'bg-amber-500 text-white' : 
                                'bg-white/10 text-white/60'
                              }`}>
                                {isPastStep ? (
                                  stepCompleted ? <Check className="w-4 h-4" /> : <AlertTriangle className="w-4 h-4" />
                                ) : (
                                  <StepIcon className="w-4 h-4" />
                                )}
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className={`text-sm font-medium truncate ${isActive ? 'text-white' : ''}`}>
                                  {step.title}
                                </p>
                                <p className="text-xs text-white/50">Step {index + 1}</p>
                              </div>
                            </button>
                          );
                        })}
                      </nav>
                    </div>
                  </div>

                  {/* Form Content */}
                  <div className="flex-1 p-6 md:p-8">
                    <form onSubmit={handleQuoteSubmit}>
                      {/* File Upload */}
                      <MultiFileUpload
                        files={formData.files}
                        onFilesChange={(files) => {
                          setFormData({...formData, files});
                          setDetectedUnits({ units: null, message: null });
                          setSelectedFileIndex(0);
                          if (files.length <= 1) setMultiFileSettings('');
                        }}
                        fileInputId="file-upload"
                        accept=".stl,.obj,.3mf,.ply,.amf,.step,.stp"
                        emptyIcon={Rotate3d}
                        emptyTitle="Upload your 3D model"
                        emptySubtitle="You can view and verify your 3D model here"
                        emptyButtonText="Browse 3D Files"
                        emptyNote="STL, OBJ, 3MF, STEP, PLY files supported"
                        renderPreview={(file) => (
                          <ThreeDPreview 
                            file={file}
                            material={formData.material}
                            strength={formData.strength}
                            quality={formData.quality}
                            units={formData.units}
                            markersEnabled={quoteStep === 1 && formData.insertsRequired === 'yes' && markersMode}
                            markers={formData.inserts}
                            onAddMarker={(position) => {
                              setFormData(prev => ({
                                ...prev,
                                inserts: [...prev.inserts, {
                                  id: `mk_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
                                  position,
                                  type: '',
                                  size: '',
                                  customSize: '',
                                  note: ''
                                }]
                              }));
                            }}
                            onRemoveMarker={(id) => {
                              setFormData(prev => ({
                                ...prev,
                                inserts: prev.inserts.filter(m => m.id !== id)
                              }));
                            }}
                            onAnalysis={(analysisData) => {
                              if (analysisData.detectedUnits) {
                                setDetectedUnits({
                                  units: analysisData.detectedUnits.units,
                                  message: analysisData.detectedUnits.message
                                });
                                if (analysisData.detectedUnits.units !== 'unknown') {
                                  setFormData(prev => ({...prev, units: analysisData.detectedUnits.units}));
                                }
                              }
                            }}
                            onFileModified={(modifiedFile) => {
                              setFormData(prev => {
                                const updated = [...prev.files];
                                const idx = updated.indexOf(file);
                                if (idx >= 0) updated[idx] = modifiedFile;
                                return { ...prev, files: updated };
                              });
                            }}
                          />
                        )}
                      />
                      <div className="flex items-start gap-2 -mt-6 mb-8 text-xs text-slate-500">
                        <Lock className="w-4 h-4 text-slate-700 flex-shrink-0 mt-0.5" />
                        <p><span className="font-bold text-slate-700">You own your ideas and designs.</span> All uploads are confidential and not shared with any third parties.</p>
                      </div>

                      {/* Multi-file settings mode */}
                      {formData.files.length > 1 && (
                        <div className="mb-8 pb-8 border-b border-slate-200">
                          <h3 className="text-base font-semibold text-slate-900 mb-3">How would you like to configure your parts?</h3>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            <button
                              type="button"
                              onClick={() => setMultiFileSettings('same')}
                              className={`p-4 rounded-xl border-2 text-left transition-all ${
                                multiFileSettings === 'same'
                                  ? 'border-[#0007b5] bg-blue-50/50 shadow-md'
                                  : 'border-slate-200 hover:border-slate-300'
                              }`}
                            >
                              <p className="font-semibold text-slate-900">Same settings for all</p>
                              <p className="text-sm text-slate-500 mt-1">Apply the same material, quality, and options to all {formData.files.length} files</p>
                            </button>
                            <button
                              type="button"
                              onClick={() => setMultiFileSettings('different')}
                              className={`p-4 rounded-xl border-2 text-left transition-all ${
                                multiFileSettings === 'different'
                                  ? 'border-[#0007b5] bg-blue-50/50 shadow-md'
                                  : 'border-slate-200 hover:border-slate-300'
                              }`}
                            >
                              <p className="font-semibold text-slate-900">Different settings per part</p>
                              <p className="text-sm text-slate-500 mt-1">Configure and add each part to cart individually</p>
                            </button>
                          </div>

                          {multiFileSettings === 'different' && (
                            <div className="mt-4 p-4 bg-amber-50 border border-amber-200 rounded-xl">
                              <p className="text-sm font-semibold text-amber-800 mb-2">How it works:</p>
                              <ol className="text-sm text-amber-800 list-decimal list-inside space-y-1">
                                <li>Configure the settings for the currently selected part</li>
                                <li>Get an Instant Quote, then add it to cart</li>
                                <li>That part will be removed from your uploaded parts list</li>
                                <li>Configure the next part with different settings</li>
                                <li>Repeat until all parts are in your cart</li>
                              </ol>
                            </div>
                          )}
                        </div>
                      )}

                      {/* Wizard Steps */}
                      <div className="relative overflow-hidden min-h-[320px] px-1 pb-1">
                        {/* Step 0: Material & Quality */}
                        <div className={`transition-all duration-300 ease-out ${
                          quoteStep === 0 ? 'opacity-100 translate-x-0' : quoteStep > 0 ? 'opacity-0 -translate-x-full absolute inset-0' : 'opacity-0 translate-x-full absolute inset-0'
                        }`}>
                          <h3 className="text-lg font-semibold text-slate-900 mb-6">Material & Quality</h3>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                              <Label className="text-base font-medium">Material</Label>
                              <MaterialSwatchSelector
                                value={formData.material}
                                onChange={(v) => setFormData({...formData, material: v})}
                                inventory={materialInventory}
                                catalog={materialCatalog}
                              />
                              {(() => {
                                const inv = materialInventory.find(
                                  (m) => m.material_id === formData.material
                                );
                                if (!inv) return null;
                                const isOutOfStock = !inv.available && !inv.more_coming;
                                const isMoreComing = !inv.available && inv.more_coming;
                                const wks = inv.restock_weeks;
                                if (!isOutOfStock && !isMoreComing) return null;
                                const note = isMoreComing
                                  ? !wks || wks < 1
                                    ? 'Estimated availability soon'
                                    : wks === 1
                                      ? 'Estimated availability in 1 week'
                                      : `Estimated availability in ${wks} weeks`
                                  : 'Currently out of stock';
                                return (
                                  <div className="mt-2 space-y-2">
                                    <p
                                      className={`text-sm flex items-center gap-1.5 ${isOutOfStock ? 'text-red-700' : 'text-amber-700'}`}
                                      data-testid="material-restock-note"
                                    >
                                      <Clock className="w-4 h-4" />
                                      {note}
                                    </p>
                                    {!notifyOpen ? (
                                      <button
                                        type="button"
                                        onClick={() => setNotifyOpen(true)}
                                        className="inline-flex items-center gap-1.5 rounded-md border border-[#0007b5] text-[#0007b5] bg-white hover:bg-[#0007b5]/5 px-3 py-1.5 text-sm font-semibold transition"
                                        data-testid="material-notify-me-btn"
                                      >
                                        Notify me when back in stock
                                      </button>
                                    ) : notifyStatus.type === 'success' ? (
                                      <p
                                        className="text-sm text-green-700 flex items-center gap-1.5"
                                        data-testid="material-notify-me-success"
                                      >
                                        <Check className="w-4 h-4" />
                                        {notifyStatus.message || "We'll email you as soon as it's back."}
                                      </p>
                                    ) : (
                                      <div className="flex flex-wrap items-center gap-2" data-testid="material-notify-me-form">
                                        <Input
                                          type="email"
                                          value={notifyEmail}
                                          onChange={(e) => setNotifyEmail(e.target.value)}
                                          placeholder="you@example.com"
                                          className="w-64 h-9"
                                          data-testid="material-notify-me-email"
                                        />
                                        <Button
                                          size="sm"
                                          onClick={async () => {
                                            const email = (notifyEmail || '').trim().toLowerCase();
                                            if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
                                              setNotifyStatus({ type: 'error', message: 'Enter a valid email' });
                                              return;
                                            }
                                            try {
                                              const res = await fetch(
                                                `${API}/materials/${encodeURIComponent(formData.material)}/waitlist`,
                                                {
                                                  method: 'POST',
                                                  headers: { 'Content-Type': 'application/json' },
                                                  body: JSON.stringify({ email }),
                                                }
                                              );
                                              const data = await res.json().catch(() => ({}));
                                              if (res.ok) {
                                                setNotifyStatus({ type: 'success', message: data.message || "We'll email you when it's back." });
                                              } else {
                                                setNotifyStatus({ type: 'error', message: data.detail || 'Failed to subscribe' });
                                              }
                                            } catch (err) {
                                              setNotifyStatus({ type: 'error', message: 'Network error — try again.' });
                                            }
                                          }}
                                          className="bg-[#0007b5] hover:bg-[#000690]"
                                          data-testid="material-notify-me-submit"
                                        >
                                          Notify me
                                        </Button>
                                        <button
                                          type="button"
                                          onClick={() => {
                                            setNotifyOpen(false);
                                            setNotifyStatus({ type: 'idle', message: '' });
                                          }}
                                          className="text-xs text-slate-500 hover:underline"
                                        >
                                          Cancel
                                        </button>
                                        {notifyStatus.type === 'error' && (
                                          <p className="w-full text-xs text-red-600">
                                            {notifyStatus.message}
                                          </p>
                                        )}
                                      </div>
                                    )}
                                  </div>
                                );
                              })()}
                            </div>
                            <div>
                              <Label className="text-base font-medium">Print Quality</Label>
                              <StyledSelector
                                value={formData.quality}
                                onChange={(v) => setFormData({...formData, quality: v})}
                                placeholder="Choose quality"
                                options={[
                                  { id: 'Concept', name: 'Concept', subtitle: 'Quick Validation' },
                                  { id: 'Prototype', name: 'Prototype', subtitle: 'Functional Testing' },
                                  { id: 'Test/Demo', name: 'Test/Demo', subtitle: 'Demonstration Ready' },
                                  { id: 'End Use', name: 'End Use', subtitle: 'Production Quality' },
                                  { id: 'Display', name: 'Display', subtitle: 'Premium Finish' },
                                ]}
                              />
                            </div>
                          </div>
                        </div>

                        {/* Step 1: Options */}
                        <div className={`transition-all duration-300 ease-out ${
                          quoteStep === 1 ? 'opacity-100 translate-x-0' : quoteStep > 1 ? 'opacity-0 -translate-x-full absolute inset-0' : 'opacity-0 translate-x-full absolute inset-0'
                        }`}>
                          <h3 className="text-lg font-semibold text-slate-900 mb-6">Print Options</h3>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                              <Label className="text-base font-medium">Use Case</Label>
                              <StyledSelector
                                value={formData.strength}
                                onChange={(v) => setFormData({...formData, strength: v})}
                                placeholder="Choose use case"
                                options={[
                                  { id: 'Cosmetic Only', name: 'Cosmetic Only', subtitle: 'Display Model' },
                                  { id: 'Reinforced', name: 'Reinforced', subtitle: 'Light Use' },
                                  { id: 'Functional', name: 'Functional', subtitle: 'Heavy Duty' },
                                ]}
                              />
                            </div>
                            <div>
                              <Label className="text-base font-medium">Surface Smoothing</Label>
                              <StyledSelector
                                value={formData.smoothing}
                                onChange={(v) => setFormData({...formData, smoothing: v})}
                                placeholder="Choose smoothing"
                                options={[
                                  { id: 'None - As Printed', name: 'None', subtitle: 'As Printed' },
                                  { id: 'Ironing', name: 'Ironing', subtitle: 'For flat surfaces only' },
                                  { id: 'Light Sanding', name: 'Light Sanding' },
                                  { id: 'Smooth Finish', name: 'Smooth Finish' },
                                  { id: 'Premium Finish', name: 'Premium Finish' },
                                ]}
                              />
                            </div>
                          </div>

                          {/* Metal Inserts */}
                          <div className="mt-8 pt-6 border-t border-slate-200">
                            <Label className="text-base font-medium">Metal inserts required?</Label>
                            <div className="flex gap-3 mt-2">
                              {['no', 'yes'].map(opt => (
                                <button
                                  key={opt}
                                  type="button"
                                  data-testid={`inserts-required-${opt}`}
                                  onClick={() => setFormData({ ...formData, insertsRequired: opt, ...(opt === 'no' ? { inserts: [] } : {}) })}
                                  className={`px-6 py-2.5 rounded-xl border-2 font-medium text-sm capitalize transition-all ${
                                    formData.insertsRequired === opt
                                      ? 'bg-[#0007b5] text-white border-[#0007b5]'
                                      : 'bg-white text-slate-600 border-slate-200 hover:border-[#0007b5]/50'
                                  }`}
                                >
                                  {opt}
                                </button>
                              ))}
                            </div>

                            {formData.insertsRequired === 'yes' && (
                              <div className="mt-6 space-y-5">
                                {/* Units */}
                                <div>
                                  <Label className="text-sm font-medium text-slate-700">Sizing units</Label>
                                  <div className="flex gap-3 mt-2">
                                    {[
                                      { id: 'metric', label: 'Metric (M2, M3…)' },
                                      { id: 'imperial', label: 'Imperial (#4-40, 1/4-20…)' },
                                    ].map(u => (
                                      <button
                                        key={u.id}
                                        type="button"
                                        data-testid={`inserts-units-${u.id}`}
                                        onClick={() => setFormData({ ...formData, insertsUnits: u.id, inserts: formData.inserts.map(m => ({ ...m, size: '', customSize: '' })) })}
                                        className={`px-4 py-2 rounded-xl border-2 text-sm font-medium transition-all ${
                                          formData.insertsUnits === u.id
                                            ? 'bg-[#0007b5] text-white border-[#0007b5]'
                                            : 'bg-white text-slate-600 border-slate-200 hover:border-[#0007b5]/50'
                                        }`}
                                      >
                                        {u.label}
                                      </button>
                                    ))}
                                  </div>
                                </div>

                                {/* Marker placement instructions */}
                                <div className="p-4 rounded-xl bg-blue-50 border border-blue-100 flex items-start gap-3">
                                  <Pin className="w-5 h-5 text-[#0007b5] flex-shrink-0 mt-0.5" />
                                  <div className="flex-1 text-sm">
                                    <p className="font-semibold text-slate-900 mb-1">
                                      {markersMode ? 'Click on the 3D model to add a marker.' : 'Click "Mark locations on model" to place markers.'}
                                    </p>
                                    <p className="text-slate-600">Place one marker at any hole on your model where you would like to add an insert. To remove a marker, click "Delete" on the corresponding row in the list below.</p>
                                  </div>
                                  <button
                                    type="button"
                                    data-testid="toggle-markers-mode"
                                    onClick={() => setMarkersMode(!markersMode)}
                                    className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all flex-shrink-0 ${
                                      markersMode
                                        ? 'bg-white text-[#0007b5] border border-[#0007b5]'
                                        : 'bg-[#0007b5] text-white hover:bg-[#000690]'
                                    }`}
                                  >
                                    {markersMode ? 'Done placing' : 'Mark locations on model'}
                                  </button>
                                </div>

                                {/* Marker list */}
                                <div>
                                  <div className="flex items-center justify-between mb-3">
                                    <Label className="text-sm font-medium text-slate-700">
                                      Placed markers ({formData.inserts.length})
                                    </Label>
                                    {formData.inserts.length > 0 && (
                                      <button
                                        type="button"
                                        onClick={() => setFormData({ ...formData, inserts: [] })}
                                        className="text-xs font-semibold text-slate-500 hover:text-red-600 transition-colors"
                                      >
                                        Clear all
                                      </button>
                                    )}
                                  </div>

                                  {formData.inserts.length === 0 ? (
                                    <div className="p-6 text-center text-sm text-slate-400 bg-slate-50 border border-dashed border-slate-200 rounded-xl">
                                      No markers placed yet. Use the 3D viewer to click insert locations on your part.
                                    </div>
                                  ) : (
                                    <div className="space-y-2">
                                      {formData.inserts.map((m, i) => (
                                        <div key={m.id} className="grid grid-cols-12 gap-2 items-start p-3 bg-white border border-slate-200 rounded-xl">
                                          <div className="col-span-1 flex items-center justify-center pt-2">
                                            <span className="w-7 h-7 rounded-full bg-[#0007b5] text-white text-xs font-bold flex items-center justify-center">
                                              {i + 1}
                                            </span>
                                          </div>
                                          <div className="col-span-12 md:col-span-3">
                                            <select
                                              data-testid={`insert-type-${i}`}
                                              value={m.type}
                                              onChange={(e) => {
                                                const updated = [...formData.inserts];
                                                updated[i] = { ...updated[i], type: e.target.value };
                                                setFormData({ ...formData, inserts: updated });
                                              }}
                                              className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm focus:border-[#0007b5] focus:outline-none"
                                            >
                                              <option value="">Type…</option>
                                              <option value="threaded">Threaded insert</option>
                                              <option value="bushing">Bushing</option>
                                            </select>
                                          </div>
                                          <div className="col-span-12 md:col-span-3">
                                            <select
                                              data-testid={`insert-size-${i}`}
                                              value={m.size}
                                              onChange={(e) => {
                                                const updated = [...formData.inserts];
                                                updated[i] = { ...updated[i], size: e.target.value, ...(e.target.value !== 'Other' ? { customSize: '' } : {}) };
                                                setFormData({ ...formData, inserts: updated });
                                              }}
                                              className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm focus:border-[#0007b5] focus:outline-none"
                                            >
                                              <option value="">Size…</option>
                                              {INSERT_SIZES[formData.insertsUnits].map(s => (
                                                <option key={s} value={s}>{s}</option>
                                              ))}
                                              <option value="Other">Other</option>
                                            </select>
                                            {m.size === 'Other' && (
                                              <input
                                                type="text"
                                                data-testid={`insert-custom-size-${i}`}
                                                placeholder="Custom size"
                                                value={m.customSize || ''}
                                                onChange={(e) => {
                                                  const updated = [...formData.inserts];
                                                  updated[i] = { ...updated[i], customSize: e.target.value };
                                                  setFormData({ ...formData, inserts: updated });
                                                }}
                                                className="w-full mt-1.5 px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm focus:border-[#0007b5] focus:outline-none"
                                              />
                                            )}
                                          </div>
                                          <div className="col-span-11 md:col-span-4">
                                            <input
                                              type="text"
                                              data-testid={`insert-note-${i}`}
                                              placeholder="Note (optional)"
                                              value={m.note || ''}
                                              onChange={(e) => {
                                                const updated = [...formData.inserts];
                                                updated[i] = { ...updated[i], note: e.target.value };
                                                setFormData({ ...formData, inserts: updated });
                                              }}
                                              className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm focus:border-[#0007b5] focus:outline-none"
                                            />
                                          </div>
                                          <div className="col-span-1 flex justify-end pt-1.5">
                                            <button
                                              type="button"
                                              data-testid={`insert-delete-${i}`}
                                              onClick={() => {
                                                const updated = formData.inserts.filter((_, idx) => idx !== i);
                                                setFormData({ ...formData, inserts: updated });
                                              }}
                                              className="p-2 text-slate-400 hover:text-red-600 transition-colors rounded-lg hover:bg-red-50"
                                              title="Remove marker"
                                            >
                                              <Trash2 className="w-4 h-4" />
                                            </button>
                                          </div>
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Step 2: Color & Quantity */}
                        <div className={`transition-all duration-300 ease-out ${
                          quoteStep === 2 ? 'opacity-100 translate-x-0' : quoteStep > 2 ? 'opacity-0 -translate-x-full absolute inset-0' : 'opacity-0 translate-x-full absolute inset-0'
                        }`}>
                          <h3 className="text-lg font-semibold text-slate-900 mb-6">Color & Quantity</h3>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                              <Label className="text-base font-medium">Color</Label>
                              {(() => {
                                const inv = materialInventory.find(
                                  (m) => m.material_id === formData.material
                                );
                                // Build a lookup of per-color inventory flags for the
                                // currently selected material (if any is tracked).
                                const colorMap = new Map(
                                  (inv?.colors || []).map((c) => [c.name, c])
                                );
                                const baseColors = [
                                  { id: 'Any', name: 'Any', subtitle: 'We choose' },
                                  { id: 'Black', name: 'Black' },
                                  { id: 'White', name: 'White' },
                                  { id: 'Grey', name: 'Grey' },
                                  { id: 'Red', name: 'Red' },
                                  { id: 'Blue', name: 'Blue' },
                                  { id: 'Other', name: 'Other', subtitle: 'specify below' },
                                ];
                                const availableColors = baseColors
                                  .map((c) => {
                                    // "Any" and "Other" aren't tracked per-material
                                    if (c.id === 'Any' || c.id === 'Other') return c;
                                    const ci = colorMap.get(c.id);
                                    if (!ci) return c; // default: available
                                    if (ci.available) return c;
                                    if (ci.more_coming) {
                                      return { ...c, subtitle: 'More coming' };
                                    }
                                    return null;
                                  })
                                  .filter(Boolean);

                                const selectedColorInv = colorMap.get(formData.color);
                                const isColorMoreComing =
                                  selectedColorInv &&
                                  !selectedColorInv.available &&
                                  selectedColorInv.more_coming;
                                const colorWks = selectedColorInv?.restock_weeks;
                                const colorNote = isColorMoreComing
                                  ? !colorWks || colorWks < 1
                                    ? 'Estimated availability soon'
                                    : colorWks === 1
                                      ? 'Estimated availability in 1 week'
                                      : `Estimated availability in ${colorWks} weeks`
                                  : null;

                                return (
                                  <>
                                    <StyledSelector
                                      value={formData.color}
                                      onChange={(v) => setFormData({...formData, color: v})}
                                      placeholder="Choose color"
                                      options={availableColors}
                                    />
                                    {colorNote && (
                                      <p
                                        className="mt-2 text-sm text-amber-700 flex items-center gap-1.5"
                                        data-testid="color-restock-note"
                                      >
                                        <Clock className="w-4 h-4" />
                                        {colorNote}
                                      </p>
                                    )}
                                  </>
                                );
                              })()}
                              {formData.color === 'Other' && (
                                <Input
                                  type="text"
                                  placeholder="e.g., Green, Yellow"
                                  value={formData.customColor}
                                  onChange={(e) => setFormData({...formData, customColor: e.target.value})}
                                  className="mt-3"
                                />
                              )}
                            </div>
                            <div>
                              <Label className="text-base font-medium">Quantity</Label>
                              <input
                                type="number"
                                min="1"
                                max="1000"
                                value={formData.quantity}
                                onChange={(e) => setFormData({...formData, quantity: parseInt(e.target.value) || 1})}
                                className="w-full mt-2 px-4 py-4 bg-white border-2 border-slate-200 rounded-xl focus:border-[#0007b5] focus:outline-none text-lg font-semibold"
                              />
                            </div>
                          </div>
                        </div>

                        {/* Step 3: Contact Info */}
                        <div className={`transition-all duration-300 ease-out ${
                          quoteStep === 3 ? 'opacity-100 translate-x-0' : quoteStep > 3 ? 'opacity-0 -translate-x-full absolute inset-0' : 'opacity-0 translate-x-full absolute inset-0'
                        }`}>
                          <h3 className="text-lg font-semibold text-slate-900 mb-6">Contact Information</h3>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                              <Label className="text-base font-medium">Name</Label>
                              <input
                                type="text"
                                placeholder="Your name"
                                value={formData.contact_name}
                                onChange={(e) => setFormData({...formData, contact_name: e.target.value})}
                                className="w-full mt-2 px-4 py-4 bg-white border-2 border-slate-200 rounded-xl focus:border-[#0007b5] focus:outline-none"
                              />
                            </div>
                            <div>
                              <Label className="text-base font-medium">Email</Label>
                              <input
                                type="email"
                                placeholder="you@example.com"
                                value={formData.contact_email}
                                onChange={(e) => setFormData({...formData, contact_email: e.target.value})}
                                required
                                className="w-full mt-2 px-4 py-4 bg-white border-2 border-slate-200 rounded-xl focus:border-[#0007b5] focus:outline-none"
                              />
                            </div>
                          </div>
                        </div>

                        {/* Step 4: Shipping */}
                        <div className={`transition-all duration-300 ease-out ${
                          quoteStep === 4 ? 'opacity-100 translate-x-0' : quoteStep > 4 ? 'opacity-0 -translate-x-full absolute inset-0' : 'opacity-0 translate-x-full absolute inset-0'
                        }`}>
                          <h3 className="text-lg font-semibold text-slate-900 mb-6">Shipping Details</h3>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                              <Label className="text-base font-medium">Shipping Destination</Label>
                              <StyledSelector
                                value={formData.country_code}
                                onChange={(v) => setFormData({...formData, country_code: v})}
                                placeholder="Choose destination"
                                options={[
                                  { id: 'CA', name: 'Canada' },
                                  { id: 'US', name: 'United States' },
                                ]}
                              />
                            </div>
                            <div>
                              <Label className="text-base font-medium">Postal/Zip Code (Optional)</Label>
                              <input
                                type="text"
                                placeholder={formData.country_code === "CA" ? "A1A 1A1" : "12345"}
                                value={formData.postal_code}
                                onChange={(e) => setFormData({...formData, postal_code: e.target.value})}
                                className="w-full mt-2 px-4 py-4 bg-white border-2 border-slate-200 rounded-xl focus:border-[#0007b5] focus:outline-none"
                              />
                            </div>
                          </div>
                        </div>

                        {/* Step 5: Notes & Submit */}
                        <div className={`transition-all duration-300 ease-out ${
                          quoteStep === 5 ? 'opacity-100 translate-x-0' : quoteStep > 5 ? 'opacity-0 -translate-x-full absolute inset-0' : 'opacity-0 translate-x-full absolute inset-0'
                        }`}>
                          <h3 className="text-lg font-semibold text-slate-900 mb-6">Additional Notes</h3>
                          <div>
                            <Label className="text-base font-medium">Special Instructions (Optional)</Label>
                            <textarea
                              placeholder="Any special requirements or questions..."
                              value={formData.notes}
                              onChange={(e) => setFormData({...formData, notes: e.target.value})}
                              rows={4}
                              className="w-full mt-2 px-4 py-4 bg-white border-2 border-slate-200 rounded-xl focus:border-[#0007b5] focus:outline-none resize-none"
                            />
                          </div>

                          {/* Insert summary */}
                          {formData.insertsRequired === 'yes' && formData.inserts.length > 0 && (
                            <div className="mt-6 p-5 bg-slate-50 border border-slate-200 rounded-xl" data-testid="review-inserts-summary">
                              <div className="flex items-center gap-2 mb-3">
                                <Pin className="w-4 h-4 text-[#0007b5]" />
                                <p className="font-semibold text-slate-900 text-sm">
                                  Metal inserts ({formData.inserts.length}) — {formData.insertsUnits === 'metric' ? 'Metric' : 'Imperial'}
                                </p>
                              </div>
                              <ul className="space-y-1.5 text-sm text-slate-600">
                                {formData.inserts.map((m, i) => (
                                  <li key={m.id} className="flex items-start gap-2">
                                    <span className="w-5 h-5 rounded-full bg-[#0007b5] text-white text-[10px] font-bold flex items-center justify-center flex-shrink-0 mt-0.5">{i + 1}</span>
                                    <span>
                                      {m.type ? m.type.charAt(0).toUpperCase() + m.type.slice(1) : '—'}
                                      {m.size && ` · ${m.size === 'Other' ? (m.customSize || 'custom') : m.size}`}
                                      {m.note && <span className="text-slate-400"> — {m.note}</span>}
                                    </span>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Navigation */}
                      <div className="flex items-center justify-between mt-8 pt-6 border-t border-slate-200">
                        <button
                          type="button"
                          onClick={prevQuoteStep}
                          className={`flex items-center gap-2 px-4 py-2 text-slate-600 hover:text-slate-900 transition-colors ${quoteStep === 0 ? 'invisible' : ''}`}
                        >
                          <ArrowLeft className="w-4 h-4" />
                          Back
                        </button>
                        
                        {quoteStep < totalQuoteSteps - 1 ? (
                          <button
                            key="quote-nav-next"
                            type="button"
                            onClick={nextQuoteStep}
                            className="flex items-center gap-2 px-6 py-3 bg-[#0007b5] text-white rounded-xl hover:bg-[#000690] transition-colors font-medium"
                          >
                            Next
                            <ArrowRight className="w-4 h-4" />
                          </button>
                        ) : (
                          <button 
                            key="quote-nav-submit"
                            type="submit" 
                            disabled={loadingQuote || formData.files.length === 0 || (formData.files.length > 1 && !multiFileSettings)}
                            className={`flex items-center gap-2 px-6 py-3 rounded-xl font-medium transition-all ${
                              !loadingQuote && formData.files.length > 0 && (formData.files.length <= 1 || multiFileSettings)
                                ? 'bg-[#0007b5] text-white hover:bg-[#000690] shadow-lg shadow-blue-500/20 cursor-pointer'
                                : 'bg-[#0007b5]/40 text-white/70 cursor-not-allowed'
                            }`}
                          >
                            {loadingQuote ? "Generating Quote..." : (formData.files.length > 1 && multiFileSettings === 'same' ? "Quote All Parts" : "Get Instant Quote")}
                            <ArrowRight className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </form>
                  </div>
                </div>
              </TabsContent>

              {/* Quote Results Tab */}
              <TabsContent value="quote" className="mt-0">
                <div className="p-8">
                  {quoteData ? (
                    <Card>
                      <CardHeader>
                        <h2 className="text-2xl font-bold text-slate-900">
                          {quoteData.isMultiFile ? 'Your Multi-Part Quote' : 'Your Quote'}
                        </h2>
                      </CardHeader>
                      <CardContent>
                        {quoteData.isMultiFile ? (
                          <div>
                            {/* Line items for each file */}
                            <div className="space-y-3 mb-6">
                              {quoteData.items.map((item, idx) => (
                                <div key={idx} className="flex items-center justify-between py-3 px-4 bg-slate-50 rounded-xl">
                                  <div className="flex items-center gap-3">
                                    <div className="w-8 h-8 bg-[#0007b5]/10 rounded-lg flex items-center justify-center">
                                      <FileText className="w-4 h-4 text-[#0007b5]" />
                                    </div>
                                    <div>
                                      <p className="text-sm font-semibold text-slate-900">{item.fileName}</p>
                                      <p className="text-xs text-slate-500">Qty: {formData.quantity}</p>
                                    </div>
                                  </div>
                                  <p className="text-lg font-bold text-slate-900">${fmt(item.total_price)}</p>
                                </div>
                              ))}
                            </div>
                            {/* Total */}
                            <div className="flex items-center justify-between py-4 px-4 border-t-2 border-slate-200">
                              <p className="text-lg font-bold text-slate-900">Total ({quoteData.items.length} parts)</p>
                              <p className="text-2xl font-bold text-[#0007b5]" data-testid="quote-total-value-multi">${fmt(quoteData.total_price)}</p>
                            </div>
                            <p className="text-sm text-slate-500 mt-2 text-center">Quote sent to {formData.contact_email}</p>
                            <div className="flex flex-col sm:flex-row gap-3 justify-center items-stretch mt-6">
                              <Button
                                variant="outline"
                                onClick={handleEditQuote}
                                data-testid="edit-quote-btn-multi"
                                className="border-slate-300"
                              >
                                <ArrowLeft className="w-4 h-4 mr-2" />
                                Edit Quote
                              </Button>
                              <Button
                                className="bg-[#0007b5] hover:bg-[#000690]"
                                onClick={handleAddQuoteToCart}
                                data-testid="add-quote-to-cart-btn-multi"
                              >
                                Add All to Cart
                              </Button>
                              <Button
                                variant="ghost"
                                onClick={() => {
                                  setActiveTab('on-demand-3d-print');
                                  setQuoteStep(0);
                                  setQuoteData(null);
                                }}
                                data-testid="get-another-quote-btn-multi"
                                className="text-slate-600 hover:text-slate-900"
                              >
                                Get Another Quote
                              </Button>
                            </div>
                          </div>
                        ) : (
                          <div>
                            <div className="text-center pt-6 pb-4">
                              <p className="text-xs font-semibold text-slate-500 uppercase tracking-widest mb-2">Your Quote</p>
                              <div className="text-5xl font-bold text-[#0007b5] mb-2" data-testid="quote-total-value">
                                ${fmt(quoteData.total_price)}
                              </div>
                              {quoteData.quote_id && (
                                <p className="text-xs text-slate-500" data-testid="quote-number">
                                  Quote #{quoteData.quote_id.substring(0, 8).toUpperCase()}
                                </p>
                              )}
                              <p className="text-sm text-slate-500 mt-1">Quote sent to {formData.contact_email}</p>
                            </div>

                            {/* Part preview: model snapshot + specs */}
                            <div className="mt-6 grid grid-cols-1 md:grid-cols-5 gap-4">
                              {/* Model snapshot (left) */}
                              <div className="md:col-span-2">
                                <div className="rounded-xl overflow-hidden border border-slate-200 bg-[#f1f5f9] aspect-square flex items-center justify-center">
                                  {modelSnapshot ? (
                                    <img
                                      src={modelSnapshot}
                                      alt="Model preview"
                                      className="w-full h-full object-contain"
                                      data-testid="quote-model-snapshot"
                                    />
                                  ) : (
                                    <div className="flex flex-col items-center justify-center text-slate-400 text-xs p-6 text-center">
                                      <Boxes className="w-8 h-8 mb-2" />
                                      Preview unavailable
                                    </div>
                                  )}
                                </div>
                                <p className="text-[11px] text-slate-400 mt-2 text-center">
                                  Reference view of your part as configured
                                </p>
                              </div>

                              {/* Specs summary (right) */}
                              <div className="md:col-span-3">
                                <div className="p-5 bg-white border border-slate-200 rounded-xl h-full" data-testid="quote-specs-summary">
                                  <div className="flex items-center gap-2 mb-3">
                                    <FileText className="w-4 h-4 text-[#0007b5]" />
                                    <p className="text-sm font-semibold text-slate-900">Part specifications</p>
                                  </div>
                                  <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
                                    {quoteData.fileName && (
                                      <>
                                        <dt className="text-slate-500">File</dt>
                                        <dd className="text-slate-900 font-medium truncate" title={quoteData.fileName}>{quoteData.fileName}</dd>
                                      </>
                                    )}
                                    {formData.material && (
                                      <>
                                        <dt className="text-slate-500">Material</dt>
                                        <dd className="text-slate-900 font-medium">{formData.material}</dd>
                                      </>
                                    )}
                                    {formData.color && (
                                      <>
                                        <dt className="text-slate-500">Color</dt>
                                        <dd className="text-slate-900 font-medium">{formData.color === 'Other' ? (formData.customColor || 'Custom') : formData.color}</dd>
                                      </>
                                    )}
                                    {formData.quality && (
                                      <>
                                        <dt className="text-slate-500">Quality</dt>
                                        <dd className="text-slate-900 font-medium">{formData.quality}</dd>
                                      </>
                                    )}
                                    {formData.strength && (
                                      <>
                                        <dt className="text-slate-500">Strength</dt>
                                        <dd className="text-slate-900 font-medium">{formData.strength}</dd>
                                      </>
                                    )}
                                    {formData.smoothing && (
                                      <>
                                        <dt className="text-slate-500">Finish</dt>
                                        <dd className="text-slate-900 font-medium">{formData.smoothing}</dd>
                                      </>
                                    )}
                                    <dt className="text-slate-500">Quantity</dt>
                                    <dd className="text-slate-900 font-medium">{formData.quantity}</dd>
                                    {formData.insertsRequired === 'yes' && formData.inserts.length > 0 && (
                                      <>
                                        <dt className="text-slate-500">Metal inserts</dt>
                                        <dd className="text-slate-900 font-medium">
                                          {formData.inserts.length} · {formData.insertsUnits === 'metric' ? 'Metric' : 'Imperial'}
                                        </dd>
                                      </>
                                    )}
                                  </dl>
                                </div>
                              </div>
                            </div>

                            {/* Price breakdown */}
                            {quoteData.price_breakdown && (
                              <div className="mt-6 p-5 bg-slate-50 border border-slate-200 rounded-xl">
                                <p className="text-sm font-semibold text-slate-900 mb-3">Price breakdown</p>
                                <div className="space-y-2 text-sm">
                                  {typeof quoteData.price_breakdown.print_cost === 'number' && (
                                    <div className="flex justify-between">
                                      <span className="text-slate-600">
                                        Print cost
                                        {quoteData.price_breakdown.quantity > 1 ? ` (${quoteData.price_breakdown.quantity} parts)` : ''}
                                      </span>
                                      <span className="font-medium text-slate-900">${fmt(quoteData.price_breakdown.print_cost)}</span>
                                    </div>
                                  )}
                                  {typeof quoteData.price_breakdown.bulk_discount_amount === 'number' && quoteData.price_breakdown.bulk_discount_amount > 0 && (
                                    <div className="flex justify-between text-green-600" data-testid="bulk-discount-row">
                                      <span>
                                        Bulk discount
                                        {quoteData.price_breakdown.bulk_discount_percentage ? ` (${quoteData.price_breakdown.bulk_discount_percentage}%)` : ''}
                                      </span>
                                      <span className="font-medium">−${fmt(quoteData.price_breakdown.bulk_discount_amount)}</span>
                                    </div>
                                  )}
                                  {typeof quoteData.price_breakdown.smoothing_cost === 'number' && quoteData.price_breakdown.smoothing_cost > 0 && (
                                    <div className="flex justify-between">
                                      <span className="text-slate-600">Surface smoothing</span>
                                      <span className="font-medium text-slate-900">${fmt(quoteData.price_breakdown.smoothing_cost)}</span>
                                    </div>
                                  )}
                                  {typeof quoteData.price_breakdown.insert_cost === 'number' && quoteData.price_breakdown.insert_cost > 0 && (
                                    <div className="flex justify-between">
                                      <span className="text-slate-600">Metal inserts</span>
                                      <span className="font-medium text-slate-900">${fmt(quoteData.price_breakdown.insert_cost)}</span>
                                    </div>
                                  )}
                                  {typeof quoteData.price_breakdown.shipping_cost === 'number' && (
                                    <div className="flex justify-between">
                                      <span className="text-slate-600">Shipping</span>
                                      <span className="font-medium text-slate-900">${fmt(quoteData.price_breakdown.shipping_cost)}</span>
                                    </div>
                                  )}
                                  {typeof quoteData.price_breakdown.visible_discount_amount === 'number' && quoteData.price_breakdown.visible_discount_amount > 0 && (
                                    <div className="flex justify-between text-green-600">
                                      <span>
                                        Discount
                                        {quoteData.price_breakdown.visible_discount_name ? ` (${quoteData.price_breakdown.visible_discount_name})` : ''}
                                      </span>
                                      <span className="font-medium">−${fmt(quoteData.price_breakdown.visible_discount_amount)}</span>
                                    </div>
                                  )}
                                  <div className="flex justify-between pt-2 mt-2 border-t border-slate-200">
                                    <span className="font-semibold text-slate-900">Total</span>
                                    <span className="font-bold text-slate-900">${fmt(quoteData.total_price)}</span>
                                  </div>
                                </div>
                              </div>
                            )}

                            {/* Lead time / production info */}
                            {(quoteData.production_time || quoteData.delivery_weeks) && (
                              <>
                                <div className="mt-4 grid grid-cols-2 gap-3">
                                  {quoteData.production_time && (
                                    <div className="p-4 bg-white border border-slate-200 rounded-xl text-center">
                                      <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">Production</p>
                                      <p className="text-sm font-semibold text-slate-900">
                                        {quoteData.production_time}
                                      </p>
                                    </div>
                                  )}
                                  {quoteData.delivery_weeks != null && (
                                    <div className="p-4 bg-white border border-slate-200 rounded-xl text-center">
                                      <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">Lead time</p>
                                      <p className="text-sm font-semibold text-slate-900">
                                        {(() => {
                                          const extraWeeks = leadTimeAdjustment.value > 0
                                            ? (leadTimeAdjustment.unit === 'weeks'
                                                ? leadTimeAdjustment.value
                                                : Math.ceil(leadTimeAdjustment.value / 7))
                                            : 0;
                                          const totalWeeks = quoteData.delivery_weeks + extraWeeks;
                                          return `${totalWeeks} ${totalWeeks === 1 ? 'week' : 'weeks'}`;
                                        })()}
                                      </p>
                                    </div>
                                  )}
                                </div>
                                {leadTimeAdjustment.value > 0 && (
                                  <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-xl text-xs text-amber-800 leading-relaxed" data-testid="lead-time-notice">
                                    <strong>Note:</strong> Due to current demand, our lead time is longer than the usual 1-2 days. We are working hard to expand capacity for faster fulfillment and apologize for any inconvenience. If you would like to request rush fulfillment, please copy your quote # and send to{' '}
                                    <a
                                      href="mailto:support@create3dworks.com"
                                      className="font-semibold text-amber-900 underline underline-offset-2 hover:text-amber-950"
                                    >
                                      support@create3dworks.com
                                    </a>
                                    {' '}for a quote.
                                  </div>
                                )}
                              </>
                            )}

                            {/* Actions */}
                            <div className="mt-6 flex flex-col sm:flex-row gap-3 justify-center items-stretch">
                              <Button
                                variant="outline"
                                onClick={handleEditQuote}
                                data-testid="edit-quote-btn"
                                className="border-slate-300"
                              >
                                <ArrowLeft className="w-4 h-4 mr-2" />
                                Edit Quote
                              </Button>

                              {multiFileSettings === 'different' && formData.files.length > 0 ? (
                                <Button
                                  className="bg-[#0007b5] hover:bg-[#000690]"
                                  onClick={handleAddPartToCartAndNext}
                                  data-testid="add-part-and-next-btn"
                                >
                                  Add to Cart & Configure Next Part ({formData.files.length} remaining)
                                </Button>
                              ) : (
                                <Button
                                  className="bg-[#0007b5] hover:bg-[#000690]"
                                  onClick={handleAddQuoteToCart}
                                  data-testid="add-quote-to-cart-btn"
                                >
                                  Add to Cart
                                </Button>
                              )}

                              <Button
                                variant="ghost"
                                onClick={() => {
                                  setActiveTab('on-demand-3d-print');
                                  setQuoteStep(0);
                                  setQuoteData(null);
                                }}
                                data-testid="get-another-quote-btn"
                                className="text-slate-600 hover:text-slate-900"
                              >
                                Get Another Quote
                              </Button>
                            </div>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  ) : (
                    <div className="text-center py-12">
                      <p className="text-slate-500">Submit a quote request to see results here.</p>
                    </div>
                  )}
                </div>
              </TabsContent>

              {/* Design Tab - Full Wizard Form */}
              <TabsContent value="design" className="mt-0">
                <DesignServiceWizard onSubmit={handleServiceRequest} onBackToServices={handleBackToServices} onDirtyChange={setChildDirty} />
              </TabsContent>

              {/* 2D to 3D Tab - Full Wizard Form */}
              <TabsContent value="convert" className="mt-0">
                <ConversionServiceWizard onSubmit={handleServiceRequest} onBackToServices={handleBackToServices} onDirtyChange={setChildDirty} />
              </TabsContent>

              {/* Optimize Tab - Full Wizard Form with 3D Preview */}
              <TabsContent value="optimize" className="mt-0">
                <ServiceWizard3D 
                  title="Model Optimization"
                  description="Optimize your 3D models for better printability, strength, and efficiency."
                  serviceType="Product Optimization"
                  onSubmit={handleServiceRequest}
                  onBackToServices={handleBackToServices}
                  onDirtyChange={setChildDirty}
                />
              </TabsContent>

              {/* Renders Tab - Full Wizard Form with 3D Preview */}
              <TabsContent value="renders" className="mt-0">
                <ServiceWizard3D 
                  title="Product Renders"
                  description="Get photorealistic renders of your 3D models for marketing and presentations."
                  serviceType="Product Renders"
                  onSubmit={handleServiceRequest}
                  onBackToServices={handleBackToServices}
                  onDirtyChange={setChildDirty}
                />
              </TabsContent>
            </div>
          </Tabs>
        </div>

        {/* Bottom spacing */}
        <div className="py-16" />
          </>
        )}
      </div>

      <Footer />
    </div>
  );
};

export default ServicesToolPage;
